import {usePendingEditor} from "../hooks/useEditorNavigation";
import {regionMoney} from '../utils/businessRegionContext';
import {t, useLanguage} from '../i18n';
// src/sections/MembershipSection.jsx
// LOOP D2.0 — Membership Plans Core
// Business-side premium revamp: dashboard + popup panel for create/edit.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useLoopPaymentActivation } from "../components/finance/LoopPaymentActivation";
import {
  Crown,
  Plus,
  Search,
  RefreshCw,
  Edit3,
  MoreVertical,
  CalendarDays,
  CreditCard,
  Percent,
  Users,
  Sparkles,
  X,
  CheckCircle2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

export function parseMembershipPrice(value) {
  const text = String(value ?? "").trim();
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.replace(",", ".").split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents / 100 : null;
}

function clean(value) {
  return String(value || "").trim();
}

function num(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}


function benefitTypeValue(plan = {}) {
  const raw = clean(plan.benefitType || plan.membershipType || plan.usageLimitType || "").toLowerCase();
  if (plan.unlimitedUsage === true || raw === "unlimited" || raw === "ilimitado" || raw === "ilimitada") return "unlimited";
  if (raw === "discount" || raw === "descuento" || raw === "discount_only" || raw === "solo_descuento") return "discount";
  const included = num(plan.includedCredits ?? plan.credits ?? plan.servicesIncluded, 0);
  const discount = num(plan.discountPercent ?? plan.extraDiscountPercent, 0);
  if (included <= 0 && discount > 0) return "discount";
  return "credits";
}

function benefitLabel(value) {
  const type = clean(value).toLowerCase();
  if (type === "unlimited") return t("Uso ilimitado");
  if (type === "discount") return t("Solo descuento");
  return t("Créditos incluidos");
}

function benefitDescription(value) {
  const type = clean(value).toLowerCase();
  if (type === "unlimited") return "El cliente puede reservar servicios incluidos sin consumir créditos.";
  if (type === "discount") return "El cliente recibe un descuento, sin manejar créditos.";
  return "El cliente tiene una cantidad de servicios/créditos por ciclo.";
}

function defaultDraft() {
  return {
    name: "",
    description: "",
    price: "",
    billingCycle: "monthly",
    benefitType: "credits",
    includedCredits: "4",
    creditUnit: "servicios",
    discountPercent: "0",
    eligibleMode: "all",
    eligibleServiceIds: [],
    active: true,
    onlineSalesEnabled: false,
  };
}

function normalizePlan(id, data = {}) {
  const benefitType = benefitTypeValue(data);
  const includedCredits = benefitType === "credits" ? Math.max(0, num(data.includedCredits ?? data.credits ?? data.servicesIncluded, 0)) : 0;
  return {
    id,
    currency: clean(data.currency).toUpperCase() || null,
    name: clean(data.name || "Membresía"),
    description: clean(data.description || ""),
    active: data.active !== false,
    price: Math.max(0, num(data.price ?? data.amount ?? data.monthlyPrice, 0)),
    billingCycle: clean(data.billingCycle || data.cycle || "monthly"),
    benefitType,
    membershipType: benefitType,
    usageLimitType: benefitType === "unlimited" ? "unlimited" : benefitType === "discount" ? "discount" : "limited",
    unlimitedUsage: benefitType === "unlimited",
    includedCredits,
    creditUnit: clean(data.creditUnit || "servicios"),
    discountPercent: Math.max(0, Math.min(100, num(data.discountPercent ?? data.extraDiscountPercent, 0))),
    eligibleMode: data.eligibleMode === "selected" ? "selected" : "all",
    eligibleServiceIds: Array.isArray(data.eligibleServiceIds) ? data.eligibleServiceIds.map(String).filter(Boolean) : [],
    onlineSalesEnabled: data.onlineSalesEnabled === true,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

function cycleLabel(value) {
  const v = clean(value).toLowerCase();
  if (v === "weekly") return t("Semanal");
  if (v === "quarterly") return t("Trimestral");
  if (v === "yearly") return t("Anual");
  return t("Mensual");
}

function cyclePriceLabel(value) {
  const v = clean(value).toLowerCase();
  if (v === "weekly") return t("semana");
  if (v === "quarterly") return t("trimestre");
  if (v === "yearly") return t("año");
  return t("mes");
}

function serviceLabel(service = {}) {
  return clean(service.name || service.title || service.serviceName || service.label || "Servicio");
}

function safeServices(value) {
  return Array.isArray(value) ? value : [];
}

function planServicesText(plan, services = []) {
  if (plan.eligibleMode === "selected") {
    const count = Array.isArray(plan.eligibleServiceIds) ? plan.eligibleServiceIds.length : 0;
    return `${count} servicio${count === 1 ? "" : "s"}`;
  }
  const count = safeServices(services).length;
  return count ? "Todos los servicios" : "Todos los servicios";
}

function planSearchText(plan, services = []) {
  const serviceNames = safeServices(services)
    .filter((s) => (plan.eligibleServiceIds || []).map(String).includes(String(s.id || s.serviceId || s.name)))
    .map(serviceLabel)
    .join(" ");
  return `${plan.name} ${plan.description} ${plan.creditUnit} ${plan.billingCycle} ${benefitLabel(plan.benefitType)} ${serviceNames}`.toLowerCase();
}

function getServiceId(service = {}) {
  return String(service.id || service.serviceId || service.uid || service.name || service.title || "");
}

function MembershipPlanModal({
  businessDoc,
  open,
  draft,
  editingId,
  services,
  saving,
  error,
  activationNotice,
  onClose,
  onChange,
  onToggleService,
  onSave,
}) {
  useLanguage();
  const money = value => regionMoney({...businessDoc, ...(draft.currency ? {currency:draft.currency} : {})}).format(num(value));
  const onlineAllowed = Boolean(businessDoc) && (businessDoc.countryCode || 'PA') === 'PA' && businessDoc.onlinePaymentsEnabled !== false;
  if (!open) return null;

  const selectedServices = safeServices(services).filter((service) => {
    const id = getServiceId(service);
    return id && (draft.eligibleServiceIds || []).map(String).includes(id);
  });

  const previewName = clean(draft.name) || "Nombre del plan";
  const previewPrice = money(parseMembershipPrice(draft.price) ?? 0);
  const previewCycle = cycleLabel(draft.billingCycle);
  const benefitType = benefitTypeValue(draft);
  const isUnlimited = benefitType === "unlimited";
  const isDiscountOnly = benefitType === "discount";
  const previewCredits = Math.max(0, num(draft.includedCredits, 0));
  const previewUnit = clean(draft.creditUnit) || "servicios";
  const previewDiscount = Math.max(0, Math.min(100, num(draft.discountPercent, 0)));

  return createPortal(
    <div className="ms-modal-layer" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <aside className="ms-modal" role="dialog" aria-modal="true" aria-label={editingId ? "Editar membresía" : "Nueva membresía"}>
        <header className="ms-modal-head">
          <div className="ms-modal-title">
            <span className="ms-modal-icon"><Crown size={22} /></span>
            <div>
              <h3>{editingId ? "Editar membresía" : "Nueva membresía"}</h3>
              <p>{editingId ? "Actualiza el plan y cómo tus clientes lo usarán." : "Crea un plan claro para ingresos recurrentes."}</p>
            </div>
          </div>
          <button type="button" className="ms-icon-btn" onClick={onClose} aria-label={t("Cerrar")}>
            <X size={19} />
          </button>
        </header>

        <div className="ms-modal-body">
          {activationNotice}
          {error ? <div className="ms-state error">{t(error)}</div> : null}

          <section className="ms-form-card">
            <span className="ms-form-eyebrow">{t("Información principal")}</span>
            <label className="ms-field">
              <span>{t("Nombre del plan")}</span>
              <input value={draft.name} onChange={(e) => onChange({ name: e.target.value })} placeholder={t("Ej. Nails Club mensual")} />
              <small>{t("Será visible para tu equipo y para el cliente cuando tenga la membresía activa.")}</small>
            </label>

            <label className="ms-field">
              <span>{t("Descripción")}</span>
              <textarea value={draft.description} onChange={(e) => onChange({ description: e.target.value })} placeholder={t("Incluye 4 servicios al mes + 10% en extras.")} />
            </label>

            <div className="ms-form-grid two">
              <label className="ms-field">
                <span>{t("Precio")}</span>
                <input type="text" inputMode="decimal" value={draft.price} onChange={(e) => onChange({ price: e.target.value })} placeholder="30" />
              </label>
              <label className="ms-field">
                <span>{t("Ciclo")}</span>
                <select
                  value={draft.billingCycle}
                  onChange={(e) => {
                    const billingCycle = e.target.value;
                    onChange({
                      billingCycle,
                      ...(billingCycle !== "monthly" ? { onlineSalesEnabled: false } : {}),
                    });
                  }}
                >
                  <option value="monthly">{t("Mensual")}</option>
                  <option value="weekly">{t("Semanal")}</option>
                  <option value="quarterly">{t("Trimestral")}</option>
                  <option value="yearly">{t("Anual")}</option>
                </select>
              </label>
            </div>

            <div className="ms-availability-card">
              <div className="ms-availability-head">
                <div>
                  <span>{t("Disponibilidad")}</span>
                  <strong>{t("Define si el plan esta disponible y donde se puede comprar.")}</strong>
                </div>
              </div>

              <div className="ms-availability-grid">
    <div className="ms-plan-state-row">
                  <div className="ms-field ms-status-field">
                      <span>{t("Estado del plan")}</span>
                      <div className="ms-status-segmented" role="radiogroup" aria-label={t("Estado del plan")}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={draft.active !== false}
                          className={`ms-status-option ${draft.active !== false ? "is-active" : ""}`}
                          onClick={() => onChange({ active: true })}
                        >
                          <i className="ms-status-dot" aria-hidden="true" />
                          <span>{t("Activo")}</span>
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={draft.active === false}
                          className={`ms-status-option ${draft.active === false ? "is-paused" : ""}`}
                          onClick={() => onChange({ active: false })}
                        >
                          <i className="ms-status-dot" aria-hidden="true" />
                          <span>{t("Pausado")}</span>
                        </button>
                      </div>
                      <small>
                        {draft.active !== false
                          ? "Disponible para asignar y usar."
                          : "No se ofrecera hasta que vuelvas a activarlo."}
                      </small>
                    </div>
                </div>

                <div className={`ms-online-toggle-card ${draft.onlineSalesEnabled ? "is-on" : ""} ${draft.billingCycle !== "monthly" ? "is-disabled" : ""}`}>
                  <div className="ms-online-toggle-copy">
                    <span>{t("Venta online")}</span>
                    <strong>{draft.onlineSalesEnabled ? t("Disponible en Loop") : t("Solo disponible en el negocio")}</strong>
                    <small>
                      {!onlineAllowed ? t("Disponible presencialmente. Los cobros online todavía no están habilitados en este país.") : draft.billingCycle === "monthly"
                        ? t("Permite que tus clientes compren esta membresia desde Loop.")
                        : t("La venta online esta disponible unicamente para planes mensuales.")}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="ms-switch"
                    role="switch"
                    aria-checked={draft.onlineSalesEnabled === true}
                    aria-label={t("Venta online")}
                    disabled={!onlineAllowed || draft.billingCycle !== "monthly"}
                    onClick={() => onChange({ onlineSalesEnabled: !draft.onlineSalesEnabled })}
                  >
                    <span />
                  </button>
                </div>
              </div>
            </div>
            <div className="ms-benefit-type-grid" role="radiogroup" aria-label={t("Tipo de beneficio")}>
              {[
                { key: "credits", title: "Cr\u00e9ditos incluidos", copy: "Define cu\u00e1ntos usos incluye por ciclo." },
                { key: "unlimited", title: "Uso ilimitado", copy: "Reserva sin consumir cr\u00e9ditos." },
                { key: "discount", title: "Solo descuento", copy: "Ej. 20% de descuento." },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`ms-benefit-choice ${benefitType === option.key ? "is-active" : ""}`}
                  onClick={() => onChange({
                    benefitType: option.key,
                    membershipType: option.key,
                    usageLimitType: option.key === "unlimited" ? "unlimited" : option.key === "discount" ? "discount" : "limited",
                    unlimitedUsage: option.key === "unlimited",
                    includedCredits: option.key === "credits" ? (draft.includedCredits || "4") : "0",
                    creditUnit: draft.creditUnit || "servicios",
                  })}
                >
                  <div className="ms-benefit-choice-title">
                    <strong>{option.title}</strong>
                    {benefitType === option.key ? (
                      <span className="ms-benefit-selected" aria-label={t("Seleccionado")} title={t("Seleccionado")}>{"\u2713"}</span>
                    ) : null}
                  </div>
                  <small>{option.copy}</small>
                </button>
              ))}
            </div>

            <div className={`ms-benefit-builder is-${benefitType}`}>
              <div className="ms-benefit-builder-head">
                <div>
                  <span>{t("Configura el beneficio")}</span>
                  <strong>
                    {benefitType === "credits"
                      ? t("Servicios incluidos por ciclo")
                      : benefitType === "unlimited"
                        ? t("Uso ilimitado")
                        : "Descuento para el cliente"}
                  </strong>
                </div>
                <span className="ms-benefit-builder-type">{benefitLabel(benefitType)}</span>
              </div>

              {benefitType === "credits" ? (
                <>
                  <div className="ms-benefit-stepper-row">
                    <button
                      type="button"
                      className="ms-step-btn"
                      aria-label={t("Reducir servicios incluidos")}
                      onClick={() => onChange({
                        includedCredits: String(Math.max(0, num(draft.includedCredits, 0) - 1)),
                        creditUnit: draft.creditUnit || "servicios",
                      })}
                    >
                      -
                    </button>

                    <div className="ms-benefit-value-box">
                      <input
                        className="ms-benefit-number-input"
                        type="number"
                        min="0"
                        inputMode="numeric"
                        aria-label={t("Servicios incluidos por ciclo")}
                        value={draft.includedCredits}
                        onChange={(e) => onChange({
                          includedCredits: e.target.value,
                          creditUnit: draft.creditUnit || "servicios",
                        })}
                      />
                      <span>{previewUnit || t("servicios")}</span>
                    </div>

                    <button
                      type="button"
                      className="ms-step-btn"
                      aria-label={t("Aumentar servicios incluidos")}
                      onClick={() => onChange({
                        includedCredits: String(Math.max(0, num(draft.includedCredits, 0) + 1)),
                        creditUnit: draft.creditUnit || "servicios",
                      })}
                    >
                      +
                    </button>
                  </div>

                  <p className={`ms-benefit-builder-summary ${previewCredits < 1 ? "is-warning" : ""}`}>
                    {previewCredits < 1 ? (
                      <>{t("Este plan no tiene usos configurados. Define al menos")}<strong>{t("1 uso por ciclo")}</strong>{t("antes de guardarlo.")}</>
                    ) : (
                      <>{t("El cliente puede usar hasta")}<strong>{previewCredits} {previewUnit || t("servicios")}</strong>{t("por")}{cyclePriceLabel(draft.billingCycle)}.</>
                    )}
                  </p>

                  {previewDiscount > 0 ? (
                    <div className="ms-extra-discount-row">
                      <div>
                        <span>{t("Descuento opcional en extras")}</span>
                        <small>{t("Para servicios adicionales fuera de los incluidos.")}</small>
                      </div>
                      <div className="ms-mini-percent">
                        <button
                          type="button"
                          aria-label={t("Reducir descuento extra")}
                          onClick={() => onChange({ discountPercent: String(Math.max(0, previewDiscount - 5)) })}
                        >
                          -
                        </button>
                        <label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            inputMode="numeric"
                            aria-label={t("Descuento extra")}
                            value={draft.discountPercent}
                            onChange={(e) => onChange({ discountPercent: e.target.value })}
                          />
                          <span>%</span>
                        </label>
                        <button
                          type="button"
                          aria-label={t("Aumentar descuento extra")}
                          onClick={() => onChange({ discountPercent: String(Math.min(100, previewDiscount + 5)) })}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="ms-add-extra-discount"
                      onClick={() => onChange({ discountPercent: "10" })}
                    >
                      <span>+</span>
                      <div>
                        <strong>{t("Agregar descuento en extras")}</strong>
                        <small>{t("Opcional para servicios adicionales fuera de los incluidos.")}</small>
                      </div>
                    </button>
                  )}
                </>
              ) : null}

              {benefitType === "unlimited" ? (
                <>
                  <div className="ms-unlimited-hero">
                    <span className="ms-unlimited-check">{"\u2713"}</span>
                    <div>
                      <strong>{t("Uso ilimitado")}</strong>
                      <small>{"El cliente reserva los servicios incluidos sin consumir cr\u00e9ditos."}</small>
                    </div>
                  </div>

                  {previewDiscount > 0 ? (
                    <div className="ms-extra-discount-row">
                      <div>
                        <span>{t("Descuento opcional en extras")}</span>
                        <small>{t("Aplica a servicios adicionales.")}</small>
                      </div>
                      <div className="ms-mini-percent">
                        <button
                          type="button"
                          aria-label={t("Reducir descuento extra")}
                          onClick={() => onChange({ discountPercent: String(Math.max(0, previewDiscount - 5)) })}
                        >
                          -
                        </button>
                        <label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            inputMode="numeric"
                            aria-label={t("Descuento extra")}
                            value={draft.discountPercent}
                            onChange={(e) => onChange({ discountPercent: e.target.value })}
                          />
                          <span>%</span>
                        </label>
                        <button
                          type="button"
                          aria-label={t("Aumentar descuento extra")}
                          onClick={() => onChange({ discountPercent: String(Math.min(100, previewDiscount + 5)) })}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="ms-add-extra-discount"
                      onClick={() => onChange({ discountPercent: "10" })}
                    >
                      <span>+</span>
                      <div>
                        <strong>{t("Agregar descuento en extras")}</strong>
                        <small>{t("Opcional para servicios adicionales.")}</small>
                      </div>
                    </button>
                  )}
                </>
              ) : null}

              {benefitType === "discount" ? (
                <>
                  <div className="ms-discount-hero">
                    <button
                      type="button"
                      className="ms-step-btn"
                      aria-label={t("Reducir descuento")}
                      onClick={() => onChange({ discountPercent: String(Math.max(0, previewDiscount - 5)) })}
                    >
                      -
                    </button>

                    <label className="ms-discount-value">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        inputMode="numeric"
                        aria-label={t("Descuento")}
                        value={draft.discountPercent}
                        onChange={(e) => onChange({ discountPercent: e.target.value })}
                      />
                      <span>%</span>
                    </label>

                    <button
                      type="button"
                      className="ms-step-btn"
                      aria-label={t("Aumentar descuento")}
                      onClick={() => onChange({ discountPercent: String(Math.min(100, previewDiscount + 5)) })}
                    >
                      +
                    </button>
                  </div>

                  <p className="ms-benefit-builder-summary">{t("El cliente recibe")}<strong>{previewDiscount}{t("% de descuento")}</strong>{t("en los servicios aplicables.")}</p>
                </>
              ) : null}
            </div>

          </section>

          <section className="ms-form-card">
            <span className="ms-form-eyebrow">{t("Servicios aplicables")}</span>
            <div className="ms-choice-row">
              <button
                type="button"
                className={`ms-choice ${draft.eligibleMode !== "selected" ? "is-active" : ""}`}
                onClick={() => onChange({ eligibleMode: "all", eligibleServiceIds: [] })}
              >
                <Sparkles size={18} />
                <strong>{t("Todos los servicios")}</strong>
                <small>{t("La membresía puede usarse en cualquier servicio del negocio.")}</small>
              </button>
              <button
                type="button"
                className={`ms-choice ${draft.eligibleMode === "selected" ? "is-active" : ""}`}
                onClick={() => onChange({ eligibleMode: "selected" })}
              >
                <CheckCircle2 size={18} />
                <strong>{t("Seleccionados")}</strong>
                <small>{t("Limita el plan a servicios específicos.")}</small>
              </button>
            </div>

            {draft.eligibleMode === "selected" ? (
              <div className="ms-services-picker">
                {safeServices(services).length ? safeServices(services).map((service) => {
                  const id = getServiceId(service);
                  const selected = (draft.eligibleServiceIds || []).map(String).includes(id);
                  return (
                    <button key={id || serviceLabel(service)} type="button" className={`ms-service-chip ${selected ? "is-active" : ""}`} onClick={() => onToggleService(id)}>
                      {selected ? <CheckCircle2 size={15} /> : <Plus size={15} />}
                      {serviceLabel(service)}
                    </button>
                  );
                }) : <div className="ms-empty small">{t("No hay servicios cargados para seleccionar.")}</div>}
              </div>
            ) : null}
          </section>

          <section className="ms-preview-card">
            <span className="ms-form-eyebrow">{t("Vista previa")}</span>
            <div className="ms-preview-plan">
              <div className="ms-plan-icon"><Crown size={23} /></div>
              <div className="ms-preview-main">
                <strong>{previewName}</strong>
                <span>{previewPrice} / {cyclePriceLabel(draft.billingCycle)}</span>
                <p>{clean(draft.description) || "Descripción breve del beneficio para el cliente."}</p>
                <div className="ms-preview-benefits">
                  <span>
                    {isUnlimited
                      ? t("Uso ilimitado")
                      : isDiscountOnly
                        ? `${previewDiscount}% descuento`
                        : `${previewCredits} usos / ${cyclePriceLabel(draft.billingCycle)}`}
                  </span>
                  <span>
                    {draft.eligibleMode === "selected"
                      ? selectedServices.length === 1
                        ? serviceLabel(selectedServices[0])
                        : `${selectedServices.length} servicios aplicables`
                      : t("Todos los servicios")}
                  </span>
                  {previewDiscount > 0 && !isDiscountOnly ? (
                    <span>{previewDiscount}{t("% en extras")}</span>
                  ) : null}
                </div>

                <div className="ms-preview-meta">
                  <span className={draft.active !== false ? "is-active" : "is-paused"}>
                    <i aria-hidden="true" />
                    {draft.active !== false ? "Plan activo" : "Plan pausado"}
                  </span>
                  <span>
                    {draft.onlineSalesEnabled ? t("Venta online") : t("Solo en el negocio")}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer className="ms-modal-footer">
          <button type="button" className="ms-btn ghost" onClick={onClose}>{t("Cancelar")}</button>
          <button type="button" className="ms-btn primary" onClick={onSave} disabled={saving}>
            {saving ? t("Guardando...") : editingId ? t("Guardar cambios") : t("Crear membresia")}
          </button>
        </footer>
      </aside>
    </div>,
    document.body
  );
}

export default function MembershipSection({ active=true, onConfigurePayments, businessId, businessDoc = null, services = [] }) {
  useLanguage();
  const money = (value,currency) => regionMoney({...businessDoc,...(currency ? {currency} : {})}).format(num(value));
  const businessCurrency = regionMoney(businessDoc || {}).resolvedOptions().currency;
  const onlineAllowed = Boolean(businessDoc) && (businessDoc.countryCode || 'PA') === 'PA' && businessDoc.onlinePaymentsEnabled !== false;
  const activation = useLoopPaymentActivation(businessId, {onConfigure:onConfigurePayments, active});
  const [plans, setPlans] = useState([]);
  const [draft, setDraft] = useState(defaultDraft());
  const [editingId, setEditingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickSavingId, setQuickSavingId] = useState("");
  const [openPlanMenuId, setOpenPlanMenuId] = useState("");
  const [lifecyclePlan, setLifecyclePlan] = useState(null);
  const [lifecycleInfo, setLifecycleInfo] = useState(null);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleApplying, setLifecycleApplying] = useState(false);
  usePendingEditor({pending:active && modalOpen,busy:saving || lifecycleApplying,title:"Membresías",discard:()=>setModalOpen(false)});
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [message, setMessage] = useState("");

  const activePlans = useMemo(() => plans.filter((p) => p.active !== false), [plans]);
  const inactivePlans = useMemo(() => plans.filter((p) => p.active === false), [plans]);

  const avgPrice = useMemo(() => {
    if (!activePlans.length) return 0;
    if (activePlans.some(plan=>plan.currency && plan.currency !== businessCurrency)) return null;
    return activePlans.reduce((sum, plan) => sum + num(plan.price, 0), 0) / activePlans.length;
  }, [activePlans,businessCurrency]);

  const serviceCoverage = useMemo(() => {
    const allServices = safeServices(services).length;
    if (!plans.length) return allServices ? "0 servicios" : "Sin servicios";
    const selected = new Set();
    plans.forEach((plan) => {
      if (plan.eligibleMode !== "selected") {
        safeServices(services).forEach((s) => selected.add(getServiceId(s)));
      } else {
        (plan.eligibleServiceIds || []).forEach((id) => selected.add(String(id)));
      }
    });
    const count = selected.size || (plans.some((p) => p.eligibleMode !== "selected") ? allServices : 0);
    return allServices ? `${count}/${allServices} servicios` : "Todos";
  }, [plans, services]);

  const filteredPlans = useMemo(() => {
    const q = clean(query).toLowerCase();
    return plans.filter((plan) => {
      if (statusFilter === "active" && plan.active === false) return false;
      if (statusFilter === "inactive" && plan.active !== false) return false;
      if (!q) return true;
      return planSearchText(plan, services).includes(q);
    });
  }, [plans, query, services, statusFilter]);

  const loadPlans = async () => {
    if (!businessId) return;
    setLoading(true);
    setError("");
    try {
      const callable = httpsCallable(getFunctions(), "getBusinessMembershipPlans");
      const res = await callable({ businessId });
      const rows = Array.isArray(res?.data?.plans) ? res.data.plans : [];
      setPlans(rows.map((p) => normalizePlan(p.id, p)).sort((a, b) => String(a.name).localeCompare(String(b.name))));
    } catch (e) {
      console.error("[MembershipSection] load failed", e);
      setError("No pudimos cargar las membresías.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const startCreate = () => {
    setEditingId("");
    setDraft(defaultDraft());
    setModalError("");
    setMessage("");
    setModalOpen(true);
  };

  const startEdit = (plan) => {
    setEditingId(plan.id);
    setDraft({
      name: plan.name || "",
      currency: plan.currency || businessCurrency,
      description: plan.description || "",
      price: plan.price ? String(plan.price) : "",
      billingCycle: plan.billingCycle || "monthly",
      benefitType: plan.benefitType || benefitTypeValue(plan),
      membershipType: plan.membershipType || plan.benefitType || benefitTypeValue(plan),
      usageLimitType: plan.usageLimitType || (benefitTypeValue(plan) === "unlimited" ? "unlimited" : benefitTypeValue(plan) === "discount" ? "discount" : "limited"),
      unlimitedUsage: benefitTypeValue(plan) === "unlimited",
      includedCredits: String(plan.includedCredits ?? 0),
      creditUnit: plan.creditUnit || "servicios",
      discountPercent: String(plan.discountPercent ?? 0),
      eligibleMode: plan.eligibleMode || "all",
      eligibleServiceIds: Array.isArray(plan.eligibleServiceIds) ? plan.eligibleServiceIds : [],
      active: plan.active !== false,
      onlineSalesEnabled: plan.onlineSalesEnabled === true,
    });
    setModalError("");
    setMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId("");
    setDraft(defaultDraft());
    setModalError("");
  };

  const toggleService = (serviceId) => {
    const id = String(serviceId || "");
    if (!id) return;
    setDraft((prev) => {
      const cur = Array.isArray(prev.eligibleServiceIds) ? prev.eligibleServiceIds.map(String) : [];
      const set = new Set(cur);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, eligibleServiceIds: Array.from(set), eligibleMode: "selected" };
    });
  };

  const savePlanPayload = async ({ planId = null, sourceDraft = draft }) => {
    const price = parseMembershipPrice(sourceDraft.price);
    if (price === null) throw new Error("invalid-membership-price");
    const callable = httpsCallable(getFunctions(), "saveBusinessMembershipPlan");
    const saveResponse = await callable({
      businessId,
      planId,
      plan: {
        ...sourceDraft,
        name: clean(sourceDraft.name),
        description: clean(sourceDraft.description),
        price,
        benefitType: benefitTypeValue(sourceDraft),
        membershipType: benefitTypeValue(sourceDraft),
        usageLimitType: benefitTypeValue(sourceDraft) === "unlimited" ? "unlimited" : benefitTypeValue(sourceDraft) === "discount" ? "discount" : "limited",
        unlimitedUsage: benefitTypeValue(sourceDraft) === "unlimited",
        includedCredits: benefitTypeValue(sourceDraft) === "credits" ? Math.max(0, num(sourceDraft.includedCredits, 0)) : 0,
        discountPercent: Math.max(0, Math.min(100, num(sourceDraft.discountPercent, 0))),
        eligibleMode: sourceDraft.eligibleMode === "selected" ? "selected" : "all",
        eligibleServiceIds: sourceDraft.eligibleMode === "selected" ? (sourceDraft.eligibleServiceIds || []) : [],
        active: sourceDraft.active !== false,
        onlineSalesEnabled:
          onlineAllowed && sourceDraft.billingCycle === "monthly" &&
          sourceDraft.onlineSalesEnabled === true,
      },
    });
    return saveResponse?.data || {};
  };

  const savePlan = async () => {
    if (!businessId) return;
    if (parseMembershipPrice(draft.price) === null) {
      setModalError("Escribe un precio válido con hasta dos decimales.");
      return;
    }
    if (onlineAllowed && draft.onlineSalesEnabled && draft.active !== false && !(await activation.request())) return;
    if (!clean(draft.name)) {
      setModalError("Ponle un nombre a la membresía.");
      return;
    }    if (benefitTypeValue(draft) === "credits" && num(draft.includedCredits, 0) < 1) {
      setModalError("Define al menos 1 uso por ciclo para esta membresía.");
      return;
    }
    setSaving(true);
    setError("");
    setModalError("");
    setMessage("");
    try {
      const requestedActive = draft.active !== false;
      const saveResult = await savePlanPayload({ planId: editingId || null, sourceDraft: draft });
      const savedPlanId = String(saveResult?.id || editingId || "").trim();

      if (savedPlanId) {
        const verifyCallable = httpsCallable(getFunctions(), "getBusinessMembershipPlans");
        const verifyRes = await verifyCallable({ businessId });
        const verifyRows = Array.isArray(verifyRes?.data?.plans) ? verifyRes.data.plans : [];
        const persistedPlan = verifyRows.find((item) => String(item?.id || "") === savedPlanId) || null;

        if (!persistedPlan || (persistedPlan.active !== false) !== requestedActive) {
          if (!editingId) setEditingId(savedPlanId);
          const persistenceError = new Error("membership_active_state_not_persisted");
          persistenceError.savedPlanId = savedPlanId;
          throw persistenceError;
        }
      }

      setMessage(editingId ? "Membresia actualizada." : "Membresia creada.");
      closeModal();
      await loadPlans();
    } catch (e) {
      console.error("[MembershipSection] save failed", e);
      if (e?.message === "membership_active_state_not_persisted") {
        setModalError("El servidor no guardo el estado del plan. El plan no se duplicara: quedo abierto para corregirlo.");
      } else {
        setModalError("No pudimos guardar la membresia.");
      }
    } finally {
      setSaving(false);
    }
  };

  const togglePlanActive = async (plan) => {
    if (!businessId || !plan?.id) return;
    if (onlineAllowed && plan.active === false && plan.onlineSalesEnabled && !(await activation.request())) return;
    setQuickSavingId(plan.id);
    setError("");
    setMessage("");
    try {
      await savePlanPayload({
        planId: plan.id,
        sourceDraft: {
          ...plan,
          price: String(plan.price || 0),
          benefitType: plan.benefitType || benefitTypeValue(plan),
          membershipType: plan.membershipType || plan.benefitType || benefitTypeValue(plan),
          usageLimitType: plan.usageLimitType || (benefitTypeValue(plan) === "unlimited" ? "unlimited" : benefitTypeValue(plan) === "discount" ? "discount" : "limited"),
          unlimitedUsage: benefitTypeValue(plan) === "unlimited",
          includedCredits: String(plan.includedCredits || 0),
          discountPercent: String(plan.discountPercent || 0),
          active: plan.active === false,
        },
      });
      setMessage(plan.active === false ? "Membresía activada." : "Membresía pausada.");
      await loadPlans();
    } catch (e) {
      console.error("[MembershipSection] toggle failed", e);
      setError("No pudimos cambiar el estado de la membresía.");
    } finally {
      setQuickSavingId("");
    }
  };

  useEffect(() => {
    if (!openPlanMenuId) return undefined;

    const closeMenu = (event) => {
      if (event?.target?.closest?.("[data-ms-plan-menu]")) return;
      setOpenPlanMenuId("");
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenPlanMenuId("");
    };

    document.addEventListener("pointerdown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openPlanMenuId]);

  const inspectPlanLifecycle = async (plan) => {
    if (!businessId || !plan?.id) return;

    setOpenPlanMenuId("");
    setLifecyclePlan(plan);
    setLifecycleInfo(null);
    setLifecycleLoading(true);
    setError("");

    try {
      const callable = httpsCallable(getFunctions(), "manageBusinessMembershipPlanLifecycle");
      const result = await callable({
        businessId,
        planId: plan.id,
        mode: "inspect",
      });
      setLifecycleInfo(result?.data || null);
    } catch (e) {
      console.error("[MembershipSection] lifecycle inspect failed", e);
      setLifecyclePlan(null);
      setLifecycleInfo(null);
      setError("No pudimos revisar si esta membresia se puede eliminar.");
    } finally {
      setLifecycleLoading(false);
    }
  };

  const applyPlanLifecycle = async () => {
    const action = lifecycleInfo?.recommendedAction;
    if (!businessId || !lifecyclePlan?.id || !["delete", "archive"].includes(action)) return;

    setLifecycleApplying(true);
    setError("");

    try {
      const callable = httpsCallable(getFunctions(), "manageBusinessMembershipPlanLifecycle");
      const result = await callable({
        businessId,
        planId: lifecyclePlan.id,
        mode: "apply",
        action,
      });

      const applied = result?.data?.action;
      setLifecyclePlan(null);
      setLifecycleInfo(null);
      setMessage(
        applied === "deleted"
          ? "Membresia eliminada definitivamente."
          : "Membresia archivada de forma segura."
      );
      await loadPlans();
    } catch (e) {
      console.error("[MembershipSection] lifecycle apply failed", e);
      setError("No pudimos completar esta accion. Vuelve a revisar el plan.");
    } finally {
      setLifecycleApplying(false);
    }
  };

  const lifecycleAction = lifecycleInfo?.recommendedAction || "";
  const lifecycleCounts = lifecycleInfo?.dependencies?.counts || {};
  return (
    <section className="membership-section">
      <style>{styles}</style>

      {error ? <div className="ms-state error">{t(error)}</div> : null}
      {message ? <div className="ms-state ok">{t(message)}</div> : null}

      {lifecyclePlan ? createPortal(
        <div className="ms-lifecycle-layer" role="presentation">
          <section className="ms-lifecycle-modal" role="dialog" aria-modal="true" aria-labelledby="ms-lifecycle-title">
            <header className="ms-lifecycle-head">
              <span className={`ms-lifecycle-icon ${lifecycleAction === "delete" ? "danger" : ""}`}>
                {lifecycleAction === "delete" ? "!" : "\u2713"}
              </span>

              <div>
                <h3 id="ms-lifecycle-title">
                  {lifecycleLoading
                    ? "Revisando membresia..."
                    : lifecycleAction === "delete"
                      ? "Eliminar membresia"
                      : "Archivar membresia"}
                </h3>
                <p>{lifecyclePlan.name}</p>
              </div>

              <button
                type="button"
                className="ms-icon-btn"
                aria-label={t("Cerrar")}
                disabled={lifecycleApplying}
                onClick={() => {
                  setLifecyclePlan(null);
                  setLifecycleInfo(null);
                }}
              >
                <X size={18} />
              </button>
            </header>

            <div className="ms-lifecycle-body">
              {lifecycleLoading ? (
                <div className="ms-lifecycle-loading">{t("Revisando clientes, suscripciones, actividad y reservas...")}</div>
              ) : lifecycleAction === "delete" ? (
                <>
                  <div className="ms-lifecycle-safe">
                    <strong>{t("Se puede eliminar definitivamente.")}</strong>
                    <span>{t("No encontramos relaciones con este plan.")}</span>
                  </div>

                  <p>{t("Se eliminara el documento del plan de Loop. Esta accion no se puede deshacer.")}</p>
                </>
              ) : lifecycleAction === "archive" ? (
                <>
                  <div className="ms-lifecycle-warning">
                    <strong>{t("Este plan tiene historial relacionado.")}</strong>
                    <span>{t("Loop lo archivara en lugar de borrarlo para proteger datos existentes.")}</span>
                  </div>

                  <div className="ms-lifecycle-counts">
                    <span><strong>{lifecycleCounts.customerMemberships || 0}</strong>{" " + t("membresias de clientes")}</span>
                    <span><strong>{lifecycleCounts.membershipSubscriptions || 0}</strong>{" " + t("suscripciones")}</span>
                    <span><strong>{lifecycleCounts.membershipActivity || 0}</strong>{" " + t("eventos")}</span>
                    <span><strong>{lifecycleCounts.bookings || 0}</strong>{" " + t("reservas")}</span>
                  </div>

                  <p>
                    El plan desaparecera de esta lista y la venta online quedara desactivada.
                    Conservaremos su estado actual para no romper beneficios e historial existentes.
                  </p>
                </>
              ) : (
                <div className="ms-lifecycle-loading">{t("No hay una accion disponible.")}</div>
              )}
            </div>

            <footer className="ms-lifecycle-footer">
              <button
                type="button"
                className="ms-btn ghost"
                disabled={lifecycleApplying}
                onClick={() => {
                  setLifecyclePlan(null);
                  setLifecycleInfo(null);
                }}
              >{t("Cancelar")}</button>

              {!lifecycleLoading && ["delete", "archive"].includes(lifecycleAction) ? (
                <button
                  type="button"
                  className={`ms-btn ${lifecycleAction === "delete" ? "danger-action" : "primary"}`}
                  disabled={lifecycleApplying}
                  onClick={applyPlanLifecycle}
                >
                  {lifecycleApplying
                    ? "Procesando..."
                    : lifecycleAction === "delete"
                      ? "Eliminar definitivamente"
                      : "Archivar membresia"}
                </button>
              ) : null}
            </footer>
          </section>
        </div>,
        document.body
      ) : null}

      <div className="ms-metrics-row">
        <article className="ms-metric-card">
          <span><Crown size={20} /></span>
          <strong>{plans.length}</strong>
          <small>{t("Planes creados")}</small>
        </article>
        <article className="ms-metric-card">
          <span><ToggleRight size={20} /></span>
          <strong>{activePlans.length}</strong>
          <small>{t("Activos")}</small>
        </article>
        <article className="ms-metric-card">
          <span><CreditCard size={20} /></span>
          <strong>{avgPrice === null ? '—' : money(avgPrice)}</strong>
          <small>{t("Precio promedio")}</small>
        </article>
        <article className="ms-metric-card">
          <span><Users size={20} /></span>
          <strong>{serviceCoverage}</strong>
          <small>{t("Cobertura")}</small>
        </article>
        <button type="button" className="ms-new-card" onClick={startCreate}>
          <span><Plus size={20} /></span>
          <strong>{" " + t("Nuevo plan")}</strong>
          <small>{t("Crear membresía")}</small>
        </button>
      </div>

      <div className="ms-toolbar">
        <label className="ms-search">
          <Search size={18} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Buscar membresías...")} />
        </label>
        <select className="ms-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{t("Todos los estados")}</option>
          <option value="active">{t("Activas")}</option>
          <option value="inactive">{t("Pausadas")}</option>
        </select>
        <button type="button" className="ms-btn ghost compact" onClick={loadPlans} disabled={loading}>
          <RefreshCw size={16} /> {loading ? "Actualizando..." : t("Actualizar")}
        </button>
      </div>

      <div className="ms-list-head">
        <div>
          <span>{t("Planes activos")}</span>
          <h3>{t("Administra tus membresías")}</h3>
        </div>
        <p>{activePlans.length}{" " + t("activas ·") + " "}{inactivePlans.length}{" " + t("pausadas")}</p>
      </div>

      {loading ? <div className="ms-empty">{t("Cargando membresías...")}</div> : null}

      {!loading && plans.length === 0 ? (
        <section className="ms-empty hero-empty">
          <span><Crown size={22} /></span>
          <h3>{t("Crea tu primera membresía")}</h3>
          <p>{t("Empieza con un plan simple: créditos, uso ilimitado o descuento por ciclo.")}</p>
          <button type="button" className="ms-btn primary" onClick={startCreate}><Plus size={17} />{" " + t("Nuevo plan")}</button>
        </section>
      ) : null}

      {!loading && plans.length > 0 && filteredPlans.length === 0 ? (
        <div className="ms-empty">{t("No encontramos planes con esos filtros.")}</div>
      ) : null}

      <div className="ms-plan-list">
        {filteredPlans.map((plan) => {
          const statusClass = plan.active === false ? "is-paused" : "is-active";
          const serviceText = planServicesText(plan, services);
          const type = benefitTypeValue(plan);
          const mainBenefit = type === "unlimited" ? "Uso ilimitado" : type === "discount" ? (plan.discountPercent > 0 ? `${plan.discountPercent}% descuento` : "Solo descuento") : `${plan.includedCredits} ${plan.creditUnit}`;
          return (
            <article key={plan.id} className={`ms-plan-card ${statusClass}`}>
              <div className="ms-plan-accent">
                <Crown size={25} />
              </div>

              <div className="ms-plan-content">
                <div className="ms-plan-title-row">
                  <div>
                    <h3>{plan.name}</h3>
                    <p>{plan.description || "Plan de membresía para clientes recurrentes."}</p>
                  </div>
                  <em className={statusClass}>{plan.active === false ? t("Pausado") : t("Activo")}</em>
                </div>

                <div className="ms-plan-values">
                  <div>
                    <span>{t("Precio")}</span>
                    <strong>{money(plan.price,plan.currency)} / {cyclePriceLabel(plan.billingCycle)}</strong>
                  </div>
                  <div>
                    <span>{t("Beneficio")}</span>
                    <strong>{mainBenefit}</strong>
                  </div>
                  <div>
                    <span>{t("Servicios")}</span>
                    <strong>{serviceText}</strong>
                  </div>
                  <div>
                    <span>{t("Extra")}</span>
                    <strong>{plan.discountPercent > 0 ? `${plan.discountPercent}% descuento` : "Sin descuento"}</strong>
                  </div>
                </div>

                <div className="ms-plan-footer">
                  <div className="ms-tags">
                    <span><CalendarDays size={14} /> {cycleLabel(plan.billingCycle)}</span>
                    <span><Sparkles size={14} /> {benefitLabel(type)}</span>
                    <span><CreditCard size={14} /> {plan.onlineSalesEnabled ? t("Venta online") : t("Solo en el negocio")}</span>
                    <span><Sparkles size={14} /> {plan.eligibleMode === "selected" ? "Servicios seleccionados" : t("Todos los servicios")}</span>
                  </div>
                  <div className="ms-plan-actions">
                    <div className="ms-plan-menu-wrap" data-ms-plan-menu>
                      <button
                        type="button"
                        className="ms-circle-action"
                        aria-label={t("Mas opciones")}
                        aria-haspopup="menu"
                        aria-expanded={openPlanMenuId === plan.id}
                        onClick={() => setOpenPlanMenuId((current) => current === plan.id ? "" : plan.id)}
                      >
                        <MoreVertical size={18} />
                      </button>

                      {openPlanMenuId === plan.id ? (
                        <div className="ms-plan-menu" role="menu">
                          <button type="button" role="menuitem" onClick={() => {
                            setOpenPlanMenuId("");
                            startEdit(plan);
                          }}>
                            <Edit3 size={15} />
                            <span>{t("Editar")}</span>
                          </button>

                          <button type="button" role="menuitem" onClick={() => {
                            setOpenPlanMenuId("");
                            togglePlanActive(plan);
                          }}>
                            {plan.active === false ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                            <span>{plan.active === false ? "Activar" : "Pausar"}</span>
                          </button>

                          <div className="ms-plan-menu-divider" />

                          <button
                            type="button"
                            role="menuitem"
                            className="danger"
                            onClick={() => inspectPlanLifecycle(plan)}
                          >
                            <span className="ms-menu-danger-icon">!</span>
                            <span>{t("Eliminar o archivar")}</span>
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <button type="button" className="ms-btn ghost compact" onClick={() => startEdit(plan)}>
                      <Edit3 size={16} />{t("Editar")}</button>

                    <button type="button" className={`ms-btn compact ${plan.active === false ? "primary" : "soft"}`} onClick={() => togglePlanActive(plan)} disabled={quickSavingId === plan.id}>
                      {plan.active === false ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      {quickSavingId === plan.id ? t("Guardando...") : plan.active === false ? "Activar" : "Pausar"}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <section className="ms-tip">
        <span><Sparkles size={22} /></span>
        <div>
          <strong>{t("Tip Loop")}</strong>
          <p>{t("Los mejores planes son simples: un precio claro, pocos beneficios fáciles de entender y reglas visibles para el equipo.")}</p>
        </div>
        <button type="button" className="ms-btn ghost compact" onClick={startCreate}>{t("Crear otro plan")}</button>
      </section>

      <MembershipPlanModal
        businessDoc={businessDoc}
        open={active && modalOpen}
        draft={draft}
        editingId={editingId}
        services={services}
        saving={saving}
        activationNotice={activation.dialog}
        error={modalError}
        onClose={closeModal}
        onChange={(patch) => {
          if (patch.onlineSalesEnabled === true && !onlineAllowed) return;
          if (patch.onlineSalesEnabled === true) { activation.request(() => setDraft(prev => ({ ...prev, ...patch }))); return; }
          setDraft(prev => ({ ...prev, ...patch }));
        }}
        onToggleService={toggleService}
        onSave={savePlan}
      />
      {!modalOpen && activation.dialog}
    </section>
  );
}

const styles = `
  .membership-section{
    display:grid;
    gap:18px;
    color:#0f172a;
  }

  .ms-metrics-row{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr)) minmax(190px,.75fr);
    gap:14px;
    align-items:stretch;
  }

  .ms-metric-card,
  .ms-new-card{
    border:1px solid rgba(219,231,239,.95);
    border-radius:24px;
    background:rgba(255,255,255,.88);
    min-height:104px;
    padding:18px 20px;
    box-shadow:0 18px 38px rgba(15,23,42,.045);
    display:grid;
    grid-template-columns:auto 1fr;
    grid-template-areas:
      "icon value"
      "icon label";
    column-gap:14px;
    align-content:center;
    text-align:left;
  }

  .ms-metric-card span,
  .ms-new-card span{
    grid-area:icon;
    width:50px;
    height:50px;
    border-radius:18px;
    display:grid;
    place-items:center;
    background:#e5fbf4;
    color:#00796f;
  }

  .ms-metric-card strong,
  .ms-new-card strong{
    grid-area:value;
    align-self:end;
    font-size:28px;
    line-height:1;
    letter-spacing:-.05em;
    font-weight:950;
    color:#0f172a;
  }

  .ms-metric-card small,
  .ms-new-card small{
    grid-area:label;
    color:#64748b;
    font-size:12.5px;
    font-weight:900;
    margin-top:7px;
  }

  .ms-new-card{
    border-color:#00675f;
    background:linear-gradient(135deg,#00796f,#00675f);
    color:#fff;
    cursor:pointer;
    box-shadow:0 20px 36px rgba(0,103,95,.22);
  }

  .ms-new-card span{
    background:rgba(255,255,255,.14);
    color:#fff;
  }

  .ms-new-card strong,
  .ms-new-card small{
    color:#fff;
  }

  .ms-toolbar{
    display:grid;
    grid-template-columns:minmax(0,1fr) 230px auto;
    gap:12px;
    align-items:center;
  }

  .ms-search{
    border:1px solid #dbe7ef;
    background:#fff;
    border-radius:18px;
    min-height:52px;
    display:flex;
    align-items:center;
    gap:10px;
    padding:0 16px;
    box-shadow:0 12px 30px rgba(15,23,42,.035);
    color:#94a3b8;
  }

  .ms-search input{
    border:0;
    outline:0;
    width:100%;
    color:#0f172a;
    font-size:14px;
    font-weight:850;
    background:transparent;
  }

  .ms-search input::placeholder{color:#94a3b8}

  .ms-filter{
    min-height:52px;
    border:1px solid #dbe7ef;
    border-radius:18px;
    background:#fff;
    padding:0 16px;
    color:#0f172a;
    font-size:14px;
    font-weight:950;
    box-shadow:0 12px 30px rgba(15,23,42,.035);
  }

  .ms-btn{
    border:1px solid #dbe7ef;
    background:#fff;
    color:#0f172a;
    border-radius:16px;
    min-height:46px;
    padding:0 16px;
    font-weight:950;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    gap:8px;
    cursor:pointer;
    box-shadow:0 12px 26px rgba(15,23,42,.04);
  }

  .ms-btn.primary{
    background:#00675f;
    border-color:#00675f;
    color:#fff;
    box-shadow:0 18px 32px rgba(0,103,95,.2);
  }

  .ms-btn.soft{
    background:#f0fdfa;
    border-color:rgba(20,184,166,.25);
    color:#00675f;
  }

  .ms-btn.ghost{
    background:#fff;
    color:#00675f;
  }

  .ms-btn.compact{
    min-height:42px;
    padding:0 14px;
    border-radius:14px;
    font-size:13px;
  }

  .ms-btn:disabled{
    opacity:.55;
    cursor:not-allowed;
  }

  .ms-state{
    border-radius:18px;
    padding:12px 14px;
    font-size:13px;
    font-weight:850;
  }

  .ms-state.error{
    background:#fff7ed;
    color:#c2410c;
    border:1px solid #fed7aa;
  }

  .ms-state.ok{
    background:#f0fdfa;
    color:#00675f;
    border:1px solid #99f6e4;
  }

  .ms-list-head{
    display:flex;
    align-items:end;
    justify-content:space-between;
    gap:14px;
    margin-top:2px;
  }

  .ms-list-head span{
    display:block;
    color:#00675f;
    font-size:11px;
    font-weight:950;
    letter-spacing:.12em;
    text-transform:uppercase;
  }

  .ms-list-head h3{
    margin:3px 0 0;
    font-size:21px;
    letter-spacing:-.04em;
  }

  .ms-list-head p{
    margin:0;
    color:#64748b;
    font-size:13px;
    font-weight:850;
  }

  .ms-plan-list{
    display:grid;
    gap:14px;
  }

  .ms-plan-card{
    border:1px solid #dbe7ef;
    border-radius:28px;
    background:#fff;
    padding:22px;
    display:grid;
    grid-template-columns:72px minmax(0,1fr);
    gap:18px;
    box-shadow:0 20px 46px rgba(15,23,42,.055);
    position:relative;
    overflow:hidden;
  }

  .ms-plan-card::after{
    content:"";
    position:absolute;
    right:-90px;
    bottom:-110px;
    width:220px;
    height:220px;
    border-radius:999px;
    background:rgba(0,103,95,.07);
    pointer-events:none;
  }

  .ms-plan-card.is-paused{
    background:#fbfdff;
  }

  .ms-plan-accent{
    width:64px;
    height:64px;
    border-radius:22px;
    display:grid;
    place-items:center;
    color:#00796f;
    background:#d8fbef;
  }

  .ms-plan-content{
    min-width:0;
    position:relative;
    z-index:1;
  }

  .ms-plan-title-row{
    display:flex;
    justify-content:space-between;
    gap:14px;
    align-items:flex-start;
  }

  .ms-plan-title-row h3{
    margin:0;
    font-size:22px;
    letter-spacing:-.045em;
  }

  .ms-plan-title-row p{
    margin:6px 0 0;
    color:#64748b;
    font-size:13.5px;
    line-height:1.4;
    font-weight:800;
  }

  .ms-plan-title-row em{
    flex:0 0 auto;
    font-style:normal;
    border-radius:999px;
    padding:7px 10px;
    font-size:12px;
    font-weight:950;
  }

  .ms-plan-title-row em.is-active{
    background:#dcfce7;
    color:#047857;
  }

  .ms-plan-title-row em.is-paused{
    background:#f1f5f9;
    color:#64748b;
  }

  .ms-plan-values{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    border-top:1px solid #edf2f7;
    border-bottom:1px solid #edf2f7;
    margin-top:16px;
    padding:15px 0;
    gap:12px;
  }

  .ms-plan-values span{
    display:block;
    color:#94a3b8;
    font-size:11px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.08em;
  }

  .ms-plan-values strong{
    display:block;
    margin-top:5px;
    color:#0f172a;
    font-size:14px;
    font-weight:950;
  }

  .ms-plan-footer{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:14px;
    margin-top:16px;
  }

  .ms-tags{
    display:flex;
    flex-wrap:wrap;
    gap:8px;
  }

  .ms-tags span{
    display:inline-flex;
    align-items:center;
    gap:6px;
    border-radius:999px;
    background:#f1f5f9;
    color:#475569;
    padding:8px 10px;
    font-size:12px;
    font-weight:900;
  }

  .ms-plan-actions{
    display:flex;
    align-items:center;
    gap:9px;
    flex-wrap:wrap;
    justify-content:flex-end;
  }

  .ms-circle-action,
  .ms-icon-btn{
    width:44px;
    height:44px;
    border-radius:999px;
    border:1px solid #dbe7ef;
    background:#fff;
    color:#334155;
    display:grid;
    place-items:center;
    cursor:pointer;
    box-shadow:0 10px 24px rgba(15,23,42,.04);
  }

  .ms-empty{
    border:1px dashed #cbd5e1;
    border-radius:22px;
    padding:18px;
    color:#64748b;
    font-weight:850;
    background:#f8fafc;
  }

  .ms-empty.small{
    padding:12px;
    font-size:13px;
  }

  .ms-empty.hero-empty{
    border-style:solid;
    display:grid;
    place-items:center;
    text-align:center;
    min-height:260px;
    background:linear-gradient(135deg,#fff,#f0fdfa);
  }

  .ms-empty.hero-empty span{
    width:58px;
    height:58px;
    border-radius:22px;
    background:#d8fbef;
    color:#00796f;
    display:grid;
    place-items:center;
  }

  .ms-empty.hero-empty h3{
    margin:8px 0 0;
    font-size:22px;
    letter-spacing:-.04em;
    color:#0f172a;
  }

  .ms-empty.hero-empty p{
    margin:0;
    max-width:460px;
  }

  .ms-tip{
    border:1px solid rgba(20,184,166,.25);
    border-radius:24px;
    background:linear-gradient(135deg,#f0fdfa,#fff);
    display:flex;
    align-items:center;
    gap:14px;
    padding:18px;
    box-shadow:0 14px 34px rgba(15,23,42,.04);
  }

  .ms-tip>span{
    width:50px;
    height:50px;
    border-radius:18px;
    display:grid;
    place-items:center;
    color:#00796f;
    background:#d8fbef;
    flex:0 0 auto;
  }

  .ms-tip strong{
    display:block;
    font-size:15px;
  }

  .ms-tip p{
    margin:4px 0 0;
    color:#64748b;
    font-size:13px;
    font-weight:800;
  }

  .ms-tip .ms-btn{
    margin-left:auto;
  }

  .ms-modal-layer{
    position:fixed;
    inset:0;
    z-index:1000000;
    background:rgba(15,23,42,.36);
    backdrop-filter:blur(10px);
    display:flex;
    justify-content:flex-end;
    padding:18px;
  }

  .ms-modal{
    width:min(560px,100%);
    max-height:calc(100dvh - 36px);
    background:#fff;
    border:1px solid rgba(219,231,239,.95);
    border-radius:30px;
    box-shadow:0 34px 90px rgba(15,23,42,.22);
    display:grid;
    grid-template-rows:auto minmax(0,1fr) auto;
    overflow:hidden;
    color:#0f172a;
  }

  .ms-modal-head{
    padding:22px 22px 18px;
    border-bottom:1px solid #edf2f7;
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
  }

  .ms-modal-title{
    display:flex;
    gap:14px;
    align-items:flex-start;
  }

  .ms-modal-icon{
    width:54px;
    height:54px;
    border-radius:20px;
    display:grid;
    place-items:center;
    background:#d8fbef;
    color:#00796f;
    flex:0 0 auto;
  }

  .ms-modal-title h3{
    margin:0;
    font-size:24px;
    letter-spacing:-.055em;
  }

  .ms-modal-title p{
    margin:6px 0 0;
    color:#64748b;
    font-size:13px;
    line-height:1.4;
    font-weight:800;
  }

  .ms-modal-body{
    overflow:auto;
    padding:20px 22px 22px;
    display:grid;
    gap:14px;
    background:linear-gradient(180deg,#fff,#fbfdff);
  }

  .ms-modal-footer{
    border-top:1px solid #edf2f7;
    padding:16px 22px;
    display:flex;
    justify-content:flex-end;
    gap:10px;
    background:rgba(255,255,255,.94);
  }

  .ms-form-card,
  .ms-preview-card{
    border:1px solid #e2e8f0;
    border-radius:24px;
    background:#fff;
    padding:16px;
    box-shadow:0 12px 28px rgba(15,23,42,.035);
  }

  .ms-form-eyebrow{
    display:block;
    color:#00675f;
    font-size:11px;
    font-weight:950;
    text-transform:uppercase;
    letter-spacing:.12em;
    margin-bottom:12px;
  }

  .ms-field{
    display:grid;
    gap:6px;
    margin-top:12px;
  }

  .ms-field:first-of-type{
    margin-top:0;
  }

  .ms-field span{
    font-size:12.5px;
    color:#334155;
    font-weight:950;
  }

  .ms-field small{
    color:#64748b;
    font-size:11.5px;
    font-weight:750;
  }

  .ms-field input,
  .ms-field textarea,
  .ms-field select{
    width:100%;
    border:1px solid #dbe7ef;
    border-radius:16px;
    padding:12px 13px;
    font-size:14px;
    background:#fff;
    color:#0f172a;
    font-weight:800;
    outline:none;
  }

  .ms-field textarea{
    min-height:88px;
    resize:vertical;
    line-height:1.4;
  }

  .ms-field input:focus,
  .ms-field textarea:focus,
  .ms-field select:focus{
    border-color:#14b8a6;
    box-shadow:0 0 0 4px rgba(20,184,166,.12);
  }

  .ms-form-grid.two{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:12px;
  }

  .ms-choice-row{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:10px;
  }

  .ms-choice{
    border:1px solid #dbe7ef;
    border-radius:20px;
    background:#fff;
    min-height:112px;
    padding:14px;
    text-align:left;
    display:grid;
    gap:7px;
    color:#334155;
    cursor:pointer;
  }

  .ms-choice svg{
    color:#00796f;
  }

  .ms-choice strong{
    color:#0f172a;
    font-size:14px;
  }

  .ms-choice small{
    color:#64748b;
    font-size:12px;
    line-height:1.35;
    font-weight:750;
  }

  .ms-benefit-type-grid{
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:10px;
    margin:14px 0;
  }

  .ms-benefit-choice{
    border:1px solid #dbe4ef;
    border-radius:18px;
    background:#fff;
    padding:14px;
    text-align:left;
    cursor:pointer;
    min-height:92px;
    box-shadow:0 12px 24px rgba(15,23,42,.045);
    transition:all .18s ease;
  }

  .ms-benefit-choice strong{
    display:block;
    color:#0f172a;
    font-size:13px;
    font-weight:950;
    margin-bottom:6px;
  }

  .ms-benefit-choice small{
    display:block;
    color:#64748b;
    font-size:12px;
    line-height:1.35;
    font-weight:750;
  }

  .ms-benefit-choice.is-active{
    border-color:rgba(0,103,95,.28);
    background:linear-gradient(135deg,#f0fdfa,#ffffff);
    box-shadow:0 18px 34px rgba(0,103,95,.10);
    transform:translateY(-1px);
  }

  .ms-choice.is-active{
    border-color:#00796f;
    background:#f0fdfa;
    box-shadow:0 0 0 3px rgba(20,184,166,.10);
  }

  .ms-services-picker{
    margin-top:14px;
    display:flex;
    flex-wrap:wrap;
    gap:8px;
  }

  .ms-service-chip{
    border:1px solid #dbe7ef;
    border-radius:999px;
    background:#fff;
    min-height:38px;
    padding:0 12px;
    color:#334155;
    display:inline-flex;
    align-items:center;
    gap:7px;
    font-size:12.5px;
    font-weight:900;
    cursor:pointer;
  }

  .ms-service-chip.is-active{
    border-color:#00796f;
    background:#f0fdfa;
    color:#00675f;
  }

  .ms-preview-plan{
    border-radius:22px;
    background:linear-gradient(135deg,#f0fdfa,#fff);
    border:1px solid rgba(20,184,166,.22);
    padding:14px;
    display:flex;
    gap:12px;
  }

  .ms-plan-icon{
    width:48px;
    height:48px;
    border-radius:17px;
    display:grid;
    place-items:center;
    background:#00796f;
    color:#fff;
    flex:0 0 auto;
    box-shadow:0 14px 24px rgba(0,103,95,.18);
  }

  .ms-preview-main{
    min-width:0;
  }

  .ms-preview-main strong{
    display:block;
    font-size:15px;
    font-weight:950;
  }

  .ms-preview-main>span{
    display:block;
    color:#00675f;
    font-size:12.5px;
    font-weight:950;
    margin-top:3px;
  }

  .ms-preview-main p{
    margin:8px 0 0;
    color:#64748b;
    font-size:12.5px;
    line-height:1.35;
    font-weight:750;
  }

  .ms-preview-tags{
    display:flex;
    flex-wrap:wrap;
    gap:6px;
    margin-top:10px;
  }

  .ms-preview-tags em{
    font-style:normal;
    border-radius:999px;
    background:rgba(255,255,255,.82);
    color:#00675f;
    padding:6px 8px;
    font-size:11.5px;
    font-weight:950;
  }

  @media(max-width:1180px){
    .ms-metrics-row{grid-template-columns:repeat(2,minmax(0,1fr))}
    .ms-new-card{grid-column:1/-1}
    .ms-plan-values{grid-template-columns:repeat(2,minmax(0,1fr))}
  }

  @media(max-width:780px){
    .ms-toolbar{grid-template-columns:1fr}
    .ms-metrics-row{grid-template-columns:1fr}
    .ms-plan-card{grid-template-columns:1fr}
    .ms-plan-footer{align-items:flex-start;flex-direction:column}
    .ms-plan-actions{justify-content:flex-start}
    .ms-tip{align-items:flex-start;flex-direction:column}
    .ms-tip .ms-btn{margin-left:0}
    .ms-modal-layer{padding:10px}
    .ms-modal{border-radius:24px;max-height:calc(100dvh - 20px)}
    .ms-form-grid.two,.ms-choice-row{grid-template-columns:1fr}
  }

  @media(max-width:520px){
    .ms-plan-values{grid-template-columns:1fr}
    .ms-benefit-type-grid{grid-template-columns:1fr}
    .ms-modal-head{padding:18px}
    .ms-modal-body{padding:16px}
    .ms-modal-footer{padding:14px 16px;flex-direction:column}
    .ms-modal-footer .ms-btn{width:100%}
  }

  /* LOOP MEMBERSHIP GROWTH PREMIUM REVAMP — compact bubbles/cards for web + mobile */
  .membership-section{
    gap:14px;
  }

  .ms-metrics-row{
    grid-template-columns:repeat(5,minmax(0,1fr));
    gap:10px;
  }

  .ms-metric-card,
  .ms-new-card{
    min-height:76px;
    padding:13px 14px;
    border-radius:18px;
    grid-template-columns:36px minmax(0,1fr);
    column-gap:10px;
    box-shadow:0 10px 26px rgba(15,23,42,.035);
    background:rgba(255,255,255,.92);
  }

  .ms-metric-card span,
  .ms-new-card span{
    width:36px;
    height:36px;
    border-radius:13px;
  }

  .ms-metric-card span svg,
  .ms-new-card span svg{
    width:17px;
    height:17px;
  }

  .ms-metric-card strong,
  .ms-new-card strong{
    font-size:19px;
    line-height:1.05;
    letter-spacing:-.04em;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }

  .ms-metric-card small,
  .ms-new-card small{
    margin-top:3px;
    font-size:11px;
    line-height:1.15;
    letter-spacing:.01em;
  }

  .ms-new-card{
    border-color:rgba(0,103,95,.72);
    background:linear-gradient(135deg,#00675f,#004f47);
    box-shadow:0 14px 30px rgba(0,103,95,.16);
  }

  .ms-toolbar{
    grid-template-columns:minmax(0,1fr) 190px auto;
    gap:10px;
  }

  .ms-search,
  .ms-filter{
    min-height:44px;
    border-radius:15px;
    box-shadow:0 8px 18px rgba(15,23,42,.025);
  }

  .ms-search{padding:0 13px;}
  .ms-search input{font-size:13px;}
  .ms-filter{font-size:13px;padding:0 12px;}

  .ms-btn{
    min-height:40px;
    border-radius:14px;
    padding:0 13px;
    box-shadow:0 8px 18px rgba(15,23,42,.035);
  }

  .ms-btn.compact{
    min-height:36px;
    border-radius:12px;
    padding:0 11px;
    font-size:12px;
  }

  .ms-list-head{
    margin-top:0;
    padding:2px 2px 0;
  }

  .ms-list-head span{
    font-size:10px;
    letter-spacing:.11em;
  }

  .ms-list-head h3{
    margin-top:2px;
    font-size:18px;
  }

  .ms-list-head p{
    font-size:12px;
  }

  .ms-plan-list{
    gap:10px;
  }

  .ms-plan-card{
    border-radius:20px;
    padding:16px;
    grid-template-columns:50px minmax(0,1fr);
    gap:14px;
    box-shadow:0 12px 32px rgba(15,23,42,.045);
    overflow:hidden;
  }

  .ms-plan-card::after{
    display:none;
  }

  .ms-plan-card.is-active{
    border-color:rgba(0,103,95,.16);
  }

  .ms-plan-accent{
    width:48px;
    height:48px;
    border-radius:16px;
    background:linear-gradient(135deg,#e5fbf4,#f8fffd);
    border:1px solid rgba(0,103,95,.12);
  }

  .ms-plan-accent svg{
    width:20px;
    height:20px;
  }

  .ms-plan-title-row h3{
    font-size:19px;
  }

  .ms-plan-title-row p{
    margin-top:4px;
    font-size:12.5px;
    line-height:1.35;
    max-width:720px;
  }

  .ms-plan-title-row em{
    padding:5px 9px;
    font-size:11px;
  }

  .ms-plan-values{
    margin-top:12px;
    padding:11px 0;
    gap:8px;
  }

  .ms-plan-values span{
    font-size:10px;
    letter-spacing:.07em;
  }

  .ms-plan-values strong{
    margin-top:3px;
    font-size:13px;
    line-height:1.2;
  }

  .ms-plan-footer{
    margin-top:12px;
    gap:10px;
  }

  .ms-tags{
    gap:6px;
  }

  .ms-tags span{
    padding:6px 8px;
    font-size:11px;
    gap:5px;
    background:#f8fafc;
  }

  .ms-tags span svg{
    width:12px;
    height:12px;
  }

  .ms-plan-actions{
    gap:7px;
  }

  .ms-circle-action,
  .ms-icon-btn{
    width:36px;
    height:36px;
    box-shadow:0 8px 18px rgba(15,23,42,.035);
  }

  .ms-tip{
    border-radius:18px;
    padding:13px 14px;
    gap:11px;
    box-shadow:0 9px 22px rgba(15,23,42,.025);
  }

  .ms-tip>span{
    width:38px;
    height:38px;
    border-radius:14px;
  }

  .ms-tip>span svg{
    width:17px;
    height:17px;
  }

  .ms-tip strong{font-size:13px;}
  .ms-tip p{font-size:12px;margin-top:2px;line-height:1.35;}

  .ms-empty{
    border-radius:18px;
    padding:14px;
    font-size:13px;
  }

  .ms-empty.hero-empty{
    min-height:210px;
    border-radius:22px;
  }

  .ms-empty.hero-empty span{
    width:46px;
    height:46px;
    border-radius:16px;
  }

  .ms-empty.hero-empty h3{
    font-size:19px;
  }

  .ms-modal-layer{
    padding:14px;
  }

  .ms-modal{
    width:min(540px,100%);
    border-radius:24px;
  }

  .ms-modal-head{
    padding:18px 19px 15px;
  }

  .ms-modal-icon{
    width:42px;
    height:42px;
    border-radius:15px;
  }

  .ms-modal-icon svg{
    width:18px;
    height:18px;
  }

  .ms-modal-title{
    gap:11px;
  }

  .ms-modal-title h3{
    font-size:20px;
  }

  .ms-modal-title p{
    margin-top:3px;
    font-size:12px;
  }

  .ms-modal-body{
    padding:16px 18px 18px;
    gap:11px;
  }

  .ms-modal-footer{
    padding:12px 18px;
  }

  .ms-form-card,
  .ms-preview-card{
    border-radius:18px;
    padding:13px;
    box-shadow:0 8px 18px rgba(15,23,42,.025);
  }

  .ms-form-eyebrow{
    margin-bottom:9px;
    font-size:10px;
  }

  .ms-field{
    gap:5px;
    margin-top:10px;
  }

  .ms-field span{
    font-size:11.5px;
  }

  .ms-field input,
  .ms-field textarea,
  .ms-field select{
    min-height:42px;
    border-radius:13px;
    padding:10px 12px;
    font-size:13px;
  }

  .ms-field textarea{
    min-height:72px;
  }

  .ms-choice{
    min-height:88px;
    border-radius:16px;
    padding:12px;
    gap:5px;
  }

  .ms-benefit-type-grid{
    gap:8px;
    margin:10px 0;
  }

  .ms-benefit-choice{
    min-height:78px;
    border-radius:15px;
    padding:12px;
    box-shadow:0 8px 18px rgba(15,23,42,.025);
  }

  .ms-services-picker{
    gap:6px;
    margin-top:11px;
  }

  .ms-service-chip{
    min-height:32px;
    padding:0 10px;
    font-size:11.5px;
  }

  .ms-preview-plan{
    border-radius:17px;
    padding:12px;
  }

  .ms-plan-icon{
    width:40px;
    height:40px;
    border-radius:14px;
  }

  .ms-preview-main strong{font-size:13.5px;}
  .ms-preview-main>span{font-size:11.5px;}
  .ms-preview-main p{font-size:11.5px;margin-top:5px;}
  .ms-preview-tags{gap:5px;margin-top:7px;}
  .ms-preview-tags em{padding:5px 7px;font-size:10.5px;}

  @media(max-width:1180px){
    .ms-metrics-row{grid-template-columns:repeat(3,minmax(0,1fr));}
    .ms-new-card{grid-column:auto;}
  }

  @media(max-width:860px){
    .ms-metrics-row{grid-template-columns:repeat(2,minmax(0,1fr));}
    .ms-new-card{grid-column:1/-1;}
    .ms-toolbar{grid-template-columns:1fr;}
    .ms-plan-values{grid-template-columns:repeat(2,minmax(0,1fr));}
  }

  @media(max-width:780px){
    .membership-section{gap:12px;}
    .ms-plan-card{grid-template-columns:42px minmax(0,1fr);padding:13px;border-radius:18px;gap:11px;}
    .ms-plan-accent{width:40px;height:40px;border-radius:14px;}
    .ms-plan-title-row{gap:8px;}
    .ms-plan-title-row h3{font-size:17px;}
    .ms-plan-title-row p{font-size:12px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
    .ms-plan-title-row em{font-size:10.5px;padding:4px 8px;}
    .ms-plan-footer{align-items:stretch;flex-direction:column;}
    .ms-plan-actions{justify-content:stretch;display:grid;grid-template-columns:1fr 1fr;}
    .ms-circle-action{display:none;}
    .ms-plan-actions .ms-btn{width:100%;}
    .ms-tip{align-items:flex-start;flex-direction:column;}
    .ms-tip .ms-btn{margin-left:0;width:100%;}
    .ms-modal-layer{align-items:flex-end;justify-content:center;padding:0;background:rgba(15,23,42,.38);}
    .ms-modal{width:100%;max-height:92dvh;border-radius:24px 24px 0 0;border-bottom:0;}
    .ms-form-grid.two,.ms-choice-row{grid-template-columns:1fr;}
  }

  @media(max-width:520px){
    .ms-metrics-row{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;}
    .ms-metric-card,.ms-new-card{min-height:68px;padding:11px;border-radius:16px;grid-template-columns:32px minmax(0,1fr);}
    .ms-metric-card span,.ms-new-card span{width:32px;height:32px;border-radius:12px;}
    .ms-metric-card strong,.ms-new-card strong{font-size:16px;}
    .ms-metric-card small,.ms-new-card small{font-size:10.5px;}
    .ms-new-card{grid-column:1/-1;}
    .ms-search,.ms-filter{min-height:42px;border-radius:14px;}
    .ms-list-head{display:grid;align-items:start;gap:4px;}
    .ms-list-head h3{font-size:17px;}
    .ms-plan-values{grid-template-columns:1fr 1fr;gap:8px;}
    .ms-plan-values span{font-size:9.5px;}
    .ms-plan-values strong{font-size:12px;}
    .ms-tags span:nth-child(3){display:none;}
    .ms-benefit-type-grid{grid-template-columns:1fr;}
    .ms-modal-head{padding:16px;}
    .ms-modal-body{padding:14px;}
    .ms-modal-footer{padding:12px 14px calc(12px + env(safe-area-inset-bottom,0px));flex-direction:column;}
    .ms-modal-footer .ms-btn{width:100%;}
  }


  /* =========================================================
     LOOP MEMBERSHIP CREATE PREMIUM V62
     Premium create/edit modal + binary control cleanup.
     ========================================================= */

  .ms-status-field{
    min-width:0;
  }

  .ms-status-segmented{
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:4px;
    min-height:46px;
    padding:4px;
    border:1px solid #dfe8e7;
    border-radius:15px;
    background:#f3f7f6;
  }

  .ms-status-option{
    appearance:none;
    border:0;
    border-radius:11px;
    background:transparent;
    min-width:0;
    min-height:36px;
    padding:0 12px;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:8px;
    color:#66758a;
    font:inherit;
    font-size:12.5px;
    font-weight:850;
    cursor:pointer;
    transition:background .16s ease,box-shadow .16s ease,color .16s ease,transform .16s ease;
  }

  .ms-status-option:hover{
    color:#203047;
  }

  .ms-status-option.is-active,
  .ms-status-option.is-paused{
    background:#fff;
    color:#102337;
    box-shadow:0 5px 16px rgba(15,23,42,.08),0 0 0 1px rgba(15,23,42,.025);
  }

  .ms-status-option.is-active{
    color:#006d62;
  }

  .ms-status-option:active{
    transform:scale(.985);
  }

  .ms-status-dot{
    width:7px;
    height:7px;
    border-radius:999px;
    background:#94a3b8;
    box-shadow:0 0 0 4px rgba(148,163,184,.11);
  }

  .ms-status-option.is-active .ms-status-dot{
    background:#0f9f8f;
    box-shadow:0 0 0 4px rgba(15,159,143,.12);
  }

  .ms-status-option.is-paused .ms-status-dot{
    background:#64748b;
    box-shadow:0 0 0 4px rgba(100,116,139,.12);
  }

  .ms-online-toggle-card{
    margin-top:2px;
    border:1px solid #e2e9e8;
    border-radius:18px;
    background:linear-gradient(135deg,#fbfdfd 0%,#f6faf9 100%);
    padding:14px 15px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:18px;
    transition:border-color .16s ease,box-shadow .16s ease,background .16s ease;
  }

  .ms-online-toggle-card.is-on{
    border-color:rgba(0,130,119,.28);
    background:linear-gradient(135deg,#f2fbf9 0%,#f8fcfb 100%);
    box-shadow:0 8px 22px rgba(0,103,95,.055);
  }

  .ms-online-toggle-card.is-disabled{
    opacity:.68;
  }

  .ms-online-toggle-copy{
    min-width:0;
    display:grid;
    gap:3px;
  }

  .ms-online-toggle-copy > span{
    color:#587086;
    font-size:10px;
    font-weight:900;
    letter-spacing:.095em;
    text-transform:uppercase;
  }

  .ms-online-toggle-copy strong{
    color:#12243a;
    font-size:13px;
    line-height:1.25;
  }

  .ms-online-toggle-copy small{
    color:#64748b;
    font-size:11px;
    line-height:1.4;
    font-weight:650;
  }

  .ms-switch{
    flex:0 0 auto;
    width:48px;
    height:28px;
    border:0;
    border-radius:999px;
    padding:3px;
    background:#cbd5e1;
    cursor:pointer;
    transition:background .18s ease,box-shadow .18s ease;
  }

  .ms-switch > span{
    display:block;
    width:22px;
    height:22px;
    border-radius:50%;
    background:#fff;
    box-shadow:0 3px 9px rgba(15,23,42,.18);
    transform:translateX(0);
    transition:transform .18s ease;
  }

  .ms-online-toggle-card.is-on .ms-switch{
    background:#007d72;
    box-shadow:0 0 0 4px rgba(0,125,114,.08);
  }

  .ms-online-toggle-card.is-on .ms-switch > span{
    transform:translateX(20px);
  }

  .ms-switch:disabled{
    cursor:not-allowed;
  }

  .ms-preview-tags em.is-active{
    color:#006d62;
    border-color:rgba(0,125,114,.14);
    background:#eefaf7;
  }

  .ms-preview-tags em.is-paused{
    color:#536275;
    border-color:#e2e8f0;
    background:#f4f6f8;
  }

  @media(min-width:781px){
    .ms-modal-layer{
      padding:24px;
      align-items:center;
      justify-content:center;
      background:rgba(9,25,35,.42);
      backdrop-filter:blur(10px);
      -webkit-backdrop-filter:blur(10px);
    }

    .ms-modal{
      width:min(700px,calc(100vw - 48px));
      max-height:min(900px,calc(100dvh - 48px));
      border-radius:28px;
      border:1px solid rgba(218,228,231,.96);
      box-shadow:0 34px 90px rgba(7,23,35,.22),0 5px 18px rgba(7,23,35,.08);
      overflow:hidden;
    }

    .ms-modal-head{
      padding:18px 22px 16px;
      border-bottom:1px solid #edf2f3;
      background:rgba(255,255,255,.97);
    }

    .ms-modal-body{
      padding:18px 22px 22px;
      gap:12px;
      background:#f7faf9;
    }

    .ms-form-card,
    .ms-preview-card{
      border-radius:20px;
      padding:16px;
      border-color:#e4ebe9;
      box-shadow:0 8px 24px rgba(15,23,42,.025);
    }

    .ms-form-card{
      background:rgba(255,255,255,.98);
    }

    .ms-preview-card{
      border-color:rgba(0,125,114,.16);
      background:linear-gradient(135deg,#f2fbf9 0%,#fbfdfd 55%,#f5faf9 100%);
      box-shadow:0 10px 28px rgba(0,103,95,.055);
    }

    .ms-benefit-choice{
      border-radius:16px;
      min-height:82px;
      box-shadow:none;
    }

    .ms-benefit-choice.is-active{
      border-color:rgba(0,125,114,.48);
      background:linear-gradient(180deg,#f3fbf9,#f8fdfc);
      box-shadow:0 7px 20px rgba(0,103,95,.07);
    }

    .ms-choice{
      border-radius:17px;
      box-shadow:none;
    }

    .ms-modal-footer{
      padding:13px 22px;
      gap:9px;
      border-top:1px solid #e8efee;
      background:rgba(255,255,255,.98);
      box-shadow:0 -10px 28px rgba(15,23,42,.025);
    }

    .ms-modal-footer .ms-btn{
      min-height:42px;
      border-radius:14px;
      padding-left:18px;
      padding-right:18px;
    }

    .ms-modal-footer .ms-btn.primary{
      min-width:150px;
      box-shadow:0 8px 20px rgba(0,103,95,.14);
    }
  }

  @media(max-width:780px){
    .ms-modal-body{
      background:#f8faf9;
    }

    .ms-online-toggle-card{
      padding:13px;
    }
  }

  @media(max-width:520px){
    .ms-status-segmented{
      min-height:44px;
    }

    .ms-online-toggle-card{
      align-items:flex-start;
    }

    .ms-switch{
      margin-top:2px;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP BENEFIT BUILDER V63
     Contextual benefit configuration + compact premium preview.
     ========================================================= */

  .ms-benefit-choice{
    position:relative;
    overflow:hidden;
  }

  .ms-benefit-choice-title{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:8px;
  }

  .ms-benefit-selected{
    flex:0 0 auto;
    display:inline-flex;
    align-items:center;
    min-height:20px;
    padding:0 7px;
    border-radius:999px;
    background:#e9f8f5;
    color:#00776d;
    font-size:9px;
    font-weight:900;
    letter-spacing:.02em;
    white-space:nowrap;
  }

  .ms-benefit-builder{
    margin:2px 0 12px;
    padding:15px;
    border:1px solid #e1eae8;
    border-radius:19px;
    background:linear-gradient(145deg,#fbfdfd 0%,#f5faf9 100%);
  }

  .ms-benefit-builder.is-discount{
    border-color:rgba(0,125,114,.24);
    background:linear-gradient(145deg,#f2fbf9 0%,#fbfdfd 100%);
  }

  .ms-benefit-builder-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
    margin-bottom:13px;
  }

  .ms-benefit-builder-head > div{
    min-width:0;
    display:grid;
    gap:3px;
  }

  .ms-benefit-builder-head > div > span{
    color:#00776d;
    font-size:9.5px;
    font-weight:950;
    letter-spacing:.11em;
    text-transform:uppercase;
  }

  .ms-benefit-builder-head strong{
    color:#12243a;
    font-size:13.5px;
    line-height:1.25;
  }

  .ms-benefit-builder-type{
    flex:0 0 auto;
    border:1px solid #dbe7e5;
    border-radius:999px;
    background:#fff;
    padding:5px 9px;
    color:#52667a;
    font-size:9.5px;
    font-weight:850;
  }

  .ms-benefit-stepper-row,
  .ms-discount-hero{
    display:flex;
    align-items:center;
    justify-content:center;
    gap:10px;
  }

  .ms-step-btn{
    appearance:none;
    flex:0 0 auto;
    width:40px;
    height:40px;
    border:1px solid #d7e4e2;
    border-radius:13px;
    background:#fff;
    color:#006f66;
    font:inherit;
    font-size:20px;
    font-weight:650;
    line-height:1;
    cursor:pointer;
    box-shadow:0 5px 14px rgba(15,23,42,.035);
    transition:transform .14s ease,border-color .14s ease,box-shadow .14s ease;
  }

  .ms-step-btn:hover{
    border-color:rgba(0,125,114,.36);
    box-shadow:0 7px 18px rgba(0,103,95,.07);
  }

  .ms-step-btn:active{
    transform:scale(.96);
  }

  .ms-benefit-value-box{
    min-width:170px;
    min-height:54px;
    padding:7px 14px;
    border:1px solid rgba(0,125,114,.18);
    border-radius:16px;
    background:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:8px;
    box-shadow:0 7px 18px rgba(0,103,95,.045);
  }

  .ms-benefit-number-input,
  .ms-discount-value input,
  .ms-mini-percent input{
    appearance:textfield;
    -moz-appearance:textfield;
  }

  .ms-benefit-number-input::-webkit-inner-spin-button,
  .ms-benefit-number-input::-webkit-outer-spin-button,
  .ms-discount-value input::-webkit-inner-spin-button,
  .ms-discount-value input::-webkit-outer-spin-button,
  .ms-mini-percent input::-webkit-inner-spin-button,
  .ms-mini-percent input::-webkit-outer-spin-button{
    -webkit-appearance:none;
    margin:0;
  }

  .ms-benefit-number-input{
    width:52px;
    border:0 !important;
    outline:0;
    background:transparent;
    padding:0 !important;
    box-shadow:none !important;
    color:#0d2337;
    font:inherit;
    font-size:21px;
    font-weight:900;
    text-align:right;
  }

  .ms-benefit-value-box > span{
    color:#43566b;
    font-size:12.5px;
    font-weight:850;
  }

  .ms-benefit-builder-summary{
    margin:9px 0 0;
    text-align:center;
    color:#607287;
    font-size:10.5px;
    line-height:1.4;
    font-weight:650;
  }

  .ms-benefit-builder-summary strong{
    color:#173247;
  }

  .ms-extra-discount-row{
    margin-top:13px;
    padding-top:12px;
    border-top:1px solid #e5eceb;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:14px;
  }

  .ms-extra-discount-row > div:first-child{
    min-width:0;
    display:grid;
    gap:2px;
  }

  .ms-extra-discount-row > div:first-child > span{
    color:#24384c;
    font-size:11px;
    font-weight:850;
  }

  .ms-extra-discount-row > div:first-child > small{
    color:#718096;
    font-size:9.5px;
    line-height:1.35;
    font-weight:650;
  }

  .ms-mini-percent{
    flex:0 0 auto;
    display:flex;
    align-items:center;
    gap:5px;
    padding:3px;
    border:1px solid #dce7e5;
    border-radius:12px;
    background:#fff;
  }

  .ms-mini-percent > button{
    appearance:none;
    width:28px;
    height:28px;
    border:0;
    border-radius:9px;
    background:#f1f7f6;
    color:#00776d;
    font-size:15px;
    font-weight:850;
    cursor:pointer;
  }

  .ms-mini-percent > label{
    display:flex;
    align-items:center;
    justify-content:center;
    min-width:58px;
    gap:1px;
  }

  .ms-mini-percent input{
    width:34px;
    border:0 !important;
    outline:0;
    padding:0 !important;
    background:transparent;
    box-shadow:none !important;
    color:#11263a;
    font:inherit;
    font-size:13px;
    font-weight:900;
    text-align:right;
  }

  .ms-mini-percent label > span{
    color:#3c5368;
    font-size:12px;
    font-weight:850;
  }

  .ms-unlimited-hero{
    min-height:58px;
    padding:10px 13px;
    display:flex;
    align-items:center;
    gap:11px;
    border:1px solid rgba(0,125,114,.18);
    border-radius:16px;
    background:#fff;
  }

  .ms-unlimited-check{
    flex:0 0 auto;
    width:32px;
    height:32px;
    border-radius:11px;
    display:grid;
    place-items:center;
    background:#e9f8f5;
    color:#007b70;
    font-size:15px;
    font-weight:950;
  }

  .ms-unlimited-hero > div{
    display:grid;
    gap:2px;
  }

  .ms-unlimited-hero strong{
    color:#10263a;
    font-size:12.5px;
  }

  .ms-unlimited-hero small{
    color:#697b8f;
    font-size:10px;
    line-height:1.35;
    font-weight:650;
  }

  .ms-discount-value{
    min-width:170px;
    min-height:72px;
    padding:8px 16px;
    border:1px solid rgba(0,125,114,.22);
    border-radius:18px;
    background:#fff;
    display:flex;
    align-items:center;
    justify-content:center;
    gap:4px;
    box-shadow:0 8px 22px rgba(0,103,95,.055);
  }

  .ms-discount-value input{
    width:78px;
    border:0 !important;
    outline:0;
    padding:0 !important;
    background:transparent;
    box-shadow:none !important;
    color:#00776d;
    font:inherit;
    font-size:30px;
    font-weight:950;
    text-align:right;
  }

  .ms-discount-value > span{
    color:#00776d;
    font-size:20px;
    font-weight:900;
  }

  .ms-plan-state-row{
    display:flex;
    justify-content:flex-end;
    margin:0 0 10px;
  }

  .ms-plan-state-row .ms-status-field{
    width:min(100%,320px);
  }

  .ms-online-toggle-card{
    margin-top:0;
    padding:12px 14px;
  }

  .ms-online-toggle-copy small{
    font-size:10px;
  }

  .ms-preview-tags{
    row-gap:6px;
  }

  .ms-preview-tags em{
    white-space:nowrap;
  }

  @media(max-width:780px){
    .ms-benefit-builder{
      padding:13px;
    }

    .ms-benefit-builder-head{
      align-items:flex-start;
      margin-bottom:11px;
    }

    .ms-benefit-builder-type{
      display:none;
    }

    .ms-extra-discount-row{
      align-items:flex-start;
    }

    .ms-plan-state-row .ms-status-field{
      width:100%;
    }
  }

  @media(max-width:520px){
    .ms-benefit-stepper-row,
    .ms-discount-hero{
      gap:7px;
    }

    .ms-benefit-value-box,
    .ms-discount-value{
      min-width:0;
      flex:1 1 auto;
    }

    .ms-extra-discount-row{
      flex-direction:column;
      align-items:stretch;
    }

    .ms-mini-percent{
      width:100%;
      justify-content:space-between;
    }

    .ms-mini-percent > label{
      flex:1 1 auto;
    }

    .ms-benefit-selected{
      display:none;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP AVAILABILITY PRIORITY V64
     Commercial hierarchy: price/cycle -> availability -> benefit.
     ========================================================= */

  .ms-availability-card{
    margin:10px 0 12px;
    padding:13px;
    border:1px solid #e0e9e7;
    border-radius:19px;
    background:linear-gradient(145deg,#fbfdfd 0%,#f5faf9 100%);
  }

  .ms-availability-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:12px;
    margin-bottom:10px;
  }

  .ms-availability-head > div{
    display:grid;
    gap:2px;
  }

  .ms-availability-head span{
    color:#00776d;
    font-size:9.5px;
    font-weight:950;
    letter-spacing:.11em;
    text-transform:uppercase;
  }

  .ms-availability-head strong{
    color:#5f7185;
    font-size:10.5px;
    line-height:1.35;
    font-weight:700;
  }

  .ms-availability-grid{
    display:grid;
    grid-template-columns:minmax(0,.92fr) minmax(0,1.18fr);
    gap:10px;
    align-items:stretch;
  }

  .ms-availability-grid .ms-plan-state-row{
    margin:0;
    width:100%;
    display:block;
  }

  .ms-availability-grid .ms-status-field{
    width:100%;
    height:100%;
    padding:11px 12px;
    border:1px solid #e2e9e8;
    border-radius:16px;
    background:#fff;
    display:flex;
    flex-direction:column;
    justify-content:center;
  }

  .ms-availability-grid .ms-status-field > span{
    margin-bottom:7px;
    color:#263b4e;
    font-size:10.5px;
    font-weight:850;
  }

  .ms-availability-grid .ms-status-field > small{
    margin-top:6px;
    min-height:14px;
    color:#6d7f91;
    font-size:9.5px;
    line-height:1.35;
    font-weight:650;
  }

  .ms-availability-grid .ms-status-segmented{
    min-height:42px;
  }

  .ms-availability-grid .ms-online-toggle-card{
    margin:0;
    min-height:100%;
    padding:11px 13px;
    border-radius:16px;
    background:#fff;
  }

  .ms-availability-grid .ms-online-toggle-card.is-on{
    background:linear-gradient(135deg,#f2fbf9 0%,#fbfdfd 100%);
  }

  .ms-availability-grid .ms-online-toggle-copy > span{
    font-size:9px;
  }

  .ms-availability-grid .ms-online-toggle-copy strong{
    font-size:12px;
  }

  .ms-availability-grid .ms-online-toggle-copy small{
    font-size:9.5px;
    line-height:1.35;
  }

  @media(max-width:780px){
    .ms-availability-card{
      padding:12px;
    }

    .ms-availability-grid{
      grid-template-columns:1fr;
      gap:8px;
    }

    .ms-availability-grid .ms-status-field,
    .ms-availability-grid .ms-online-toggle-card{
      min-height:auto;
    }
  }

  @media(max-width:520px){
    .ms-availability-head strong{
      max-width:290px;
    }

    .ms-availability-grid .ms-status-field{
      padding:10px;
    }

    .ms-availability-grid .ms-online-toggle-card{
      padding:11px;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP FINAL PREMIUM POLISH V65
     Compact availability + cleaner benefit cards + refined preview.
     ========================================================= */

  /* Availability: preserve priority, reduce vertical footprint. */
  .ms-availability-card{
    margin:8px 0 10px;
    padding:11px 12px;
    border-radius:17px;
  }

  .ms-availability-head{
    margin-bottom:8px;
  }

  .ms-availability-head strong{
    font-size:9.5px;
    line-height:1.3;
  }

  .ms-availability-grid{
    gap:8px;
  }

  .ms-availability-grid .ms-status-field{
    padding:9px 10px;
    border-radius:14px;
  }

  .ms-availability-grid .ms-status-field > span{
    margin-bottom:5px;
    font-size:10px;
  }

  .ms-availability-grid .ms-status-field > small{
    margin-top:4px;
    min-height:12px;
    font-size:8.8px;
    line-height:1.3;
  }

  .ms-availability-grid .ms-status-segmented{
    min-height:38px;
    padding:3px;
    border-radius:13px;
  }

  .ms-availability-grid .ms-status-option{
    min-height:30px;
    border-radius:10px;
    padding:0 9px;
    font-size:10.5px;
  }

  .ms-availability-grid .ms-online-toggle-card{
    padding:9px 11px;
    border-radius:14px;
  }

  .ms-availability-grid .ms-online-toggle-copy{
    gap:2px;
  }

  .ms-availability-grid .ms-online-toggle-copy > span{
    font-size:8.5px;
  }

  .ms-availability-grid .ms-online-toggle-copy strong{
    font-size:11.5px;
  }

  .ms-availability-grid .ms-online-toggle-copy small{
    max-width:230px;
    font-size:8.8px;
    line-height:1.3;
  }

  .ms-availability-grid .ms-switch{
    width:44px;
    height:26px;
    padding:3px;
  }

  .ms-availability-grid .ms-switch > span{
    width:20px;
    height:20px;
  }

  .ms-availability-grid .ms-online-toggle-card.is-on .ms-switch > span{
    transform:translateX(18px);
  }

  /* Benefit cards: active card stays obvious without a long badge. */
  .ms-benefit-choice{
    min-height:82px;
    padding:12px;
    border-radius:15px;
  }

  .ms-benefit-choice-title{
    display:block;
    padding-right:24px;
  }

  .ms-benefit-choice-title strong{
    line-height:1.18;
  }

  .ms-benefit-selected{
    position:absolute;
    top:10px;
    right:10px;
    width:19px;
    height:19px;
    min-height:0;
    padding:0;
    display:grid;
    place-items:center;
    border:1px solid rgba(0,125,114,.12);
    border-radius:999px;
    background:#e7f8f4;
    color:#00776d;
    font-size:9px;
    font-weight:950;
    line-height:1;
    box-shadow:0 3px 9px rgba(0,103,95,.05);
  }

  .ms-benefit-choice.is-active{
    border-color:rgba(0,125,114,.5);
    background:linear-gradient(145deg,#f0faf8 0%,#f8fdfc 100%);
    box-shadow:0 7px 18px rgba(0,103,95,.06);
  }

  /* Preview: more like the customer-facing membership card. */
  .ms-preview-card{
    padding:14px;
  }

  .ms-preview-plan{
    padding:12px;
    border-radius:16px;
  }

  .ms-preview-main > strong{
    font-size:14px;
    line-height:1.22;
  }

  .ms-preview-main > span{
    margin-top:1px;
    color:#00776d;
    font-size:13px;
    line-height:1.15;
    font-weight:950;
  }

  .ms-preview-main > p{
    margin-top:5px;
    color:#617488;
    font-size:10.5px;
    line-height:1.35;
  }

  .ms-preview-tags{
    margin-top:7px;
    gap:5px;
  }

  .ms-preview-tags em{
    padding:4px 7px;
    border:1px solid rgba(0,125,114,.08);
    border-radius:999px;
    background:rgba(239,249,247,.72);
    color:#176158;
    font-size:9px;
    line-height:1;
    font-weight:850;
  }

  .ms-preview-tags em.is-paused{
    border-color:#e2e8f0;
    background:#f2f4f6;
    color:#64748b;
  }

  .ms-preview-tags em.is-active{
    border-color:rgba(0,125,114,.1);
    background:#eaf8f5;
    color:#00776d;
  }

  @media(max-width:780px){
    .ms-availability-card{
      padding:10px;
    }

    .ms-availability-head{
      margin-bottom:7px;
    }

    .ms-availability-grid{
      gap:7px;
    }
  }

  @media(max-width:520px){
    .ms-benefit-choice{
      min-height:76px;
    }

    .ms-benefit-choice-title{
      padding-right:22px;
    }

    .ms-preview-card{
      padding:12px;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP PREVIEW + OPTIONAL EXTRAS V66
     Cleaner zero-discount state + customer-facing preview hierarchy.
     ========================================================= */

  .ms-add-extra-discount{
    appearance:none;
    width:100%;
    margin-top:12px;
    padding:10px 11px;
    border:1px dashed rgba(0,125,114,.28);
    border-radius:14px;
    background:rgba(248,252,251,.82);
    display:flex;
    align-items:center;
    gap:10px;
    text-align:left;
    cursor:pointer;
    transition:border-color .15s ease,background .15s ease,transform .15s ease;
  }

  .ms-add-extra-discount:hover{
    border-color:rgba(0,125,114,.48);
    background:#f2faf8;
  }

  .ms-add-extra-discount:active{
    transform:scale(.995);
  }

  .ms-add-extra-discount > span{
    flex:0 0 auto;
    width:28px;
    height:28px;
    border-radius:10px;
    display:grid;
    place-items:center;
    background:#e8f7f4;
    color:#00776d;
    font-size:17px;
    font-weight:800;
  }

  .ms-add-extra-discount > div{
    min-width:0;
    display:grid;
    gap:2px;
  }

  .ms-add-extra-discount strong{
    color:#234054;
    font-size:10.5px;
    line-height:1.25;
  }

  .ms-add-extra-discount small{
    color:#718296;
    font-size:9px;
    line-height:1.3;
    font-weight:650;
  }

  .ms-preview-benefits{
    margin-top:7px;
    display:flex;
    flex-wrap:wrap;
    gap:5px;
  }

  .ms-preview-benefits > span{
    display:inline-flex;
    align-items:center;
    min-height:22px;
    padding:4px 8px;
    border:1px solid rgba(0,125,114,.09);
    border-radius:999px;
    background:rgba(236,249,246,.78);
    color:#176158;
    font-size:9px;
    line-height:1;
    font-weight:850;
    white-space:nowrap;
  }

  .ms-preview-meta{
    margin-top:8px;
    padding-top:7px;
    border-top:1px solid rgba(0,125,114,.08);
    display:flex;
    align-items:center;
    flex-wrap:wrap;
    gap:7px;
    color:#718095;
    font-size:9px;
    line-height:1.2;
    font-weight:750;
  }

  .ms-preview-meta > span{
    display:inline-flex;
    align-items:center;
    gap:5px;
  }

  .ms-preview-meta > span + span::before{
    content:"";
    width:2px;
    height:2px;
    margin-right:2px;
    border-radius:50%;
    background:#a8b4c1;
  }

  .ms-preview-meta i{
    width:6px;
    height:6px;
    border-radius:50%;
    background:#0f9f8f;
    box-shadow:0 0 0 3px rgba(15,159,143,.1);
  }

  .ms-preview-meta .is-active{
    color:#397068;
  }

  .ms-preview-meta .is-paused{
    color:#68778a;
  }

  .ms-preview-meta .is-paused i{
    background:#94a3b8;
    box-shadow:0 0 0 3px rgba(148,163,184,.12);
  }

  /* Retire old admin-style pill treatment inside the preview. */
  .ms-preview-main .ms-preview-tags{
    display:none;
  }

  @media(max-width:520px){
    .ms-add-extra-discount{
      padding:9px 10px;
    }

    .ms-preview-benefits{
      gap:4px;
    }

    .ms-preview-benefits > span{
      font-size:8.5px;
      padding:4px 7px;
    }

    .ms-preview-meta{
      gap:6px;
      font-size:8.5px;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP CREDIT SAFETY V67
     Zero-credit safety + final preview breathing room.
     ========================================================= */

  .ms-benefit-builder-summary.is-warning{
    margin-top:10px;
    padding:8px 10px;
    border:1px solid rgba(180,83,9,.14);
    border-radius:11px;
    background:#fffaf2;
    color:#8a5a18;
    text-align:left;
  }

  .ms-benefit-builder-summary.is-warning strong{
    color:#7a4a0f;
  }

  .ms-preview-card{
    padding:15px;
  }

  .ms-preview-plan{
    min-height:112px;
    padding:14px;
  }

  .ms-preview-main{
    min-width:0;
  }

  .ms-preview-main > p{
    margin-top:6px;
    margin-bottom:1px;
  }

  .ms-preview-benefits{
    margin-top:9px;
  }

  .ms-preview-meta{
    margin-top:9px;
    padding-top:8px;
  }

  @media(max-width:520px){
    .ms-preview-card{
      padding:12px;
    }

    .ms-preview-plan{
      min-height:auto;
      padding:12px;
    }
  }

  /* =========================================================
     LOOP MEMBERSHIP SAFE LIFECYCLE V69_6
     Three-dot menu + dependency-aware delete/archive.
     ========================================================= */

  .ms-plan-menu-wrap{
    position:relative;
    flex:0 0 auto;
  }

  .ms-plan-menu{
    position:absolute;
    right:0;
    bottom:calc(100% + 8px);
    z-index:50;
    width:190px;
    padding:6px;
    border:1px solid #dfe8e7;
    border-radius:14px;
    background:#fff;
    box-shadow:0 18px 46px rgba(15,23,42,.16);
  }

  .ms-plan-menu > button{
    appearance:none;
    width:100%;
    min-height:36px;
    padding:0 10px;
    border:0;
    border-radius:10px;
    background:transparent;
    display:flex;
    align-items:center;
    gap:9px;
    color:#24394b;
    font:inherit;
    font-size:11px;
    font-weight:800;
    text-align:left;
    cursor:pointer;
  }

  .ms-plan-menu > button:hover{
    background:#f3f8f7;
  }

  .ms-plan-menu > button.danger{
    color:#b42318;
  }

  .ms-plan-menu > button.danger:hover{
    background:#fff3f1;
  }

  .ms-menu-danger-icon{
    width:16px;
    height:16px;
    border-radius:6px;
    display:grid;
    place-items:center;
    background:#fff0ee;
    font-size:10px;
    font-weight:950;
  }

  .ms-plan-menu-divider{
    height:1px;
    margin:5px 4px;
    background:#edf1f2;
  }

  .ms-lifecycle-layer{
    position:fixed;
    inset:0;
    z-index:10060;
    display:grid;
    place-items:center;
    padding:20px;
    background:rgba(12,28,38,.44);
    backdrop-filter:blur(8px);
    -webkit-backdrop-filter:blur(8px);
  }

  .ms-lifecycle-modal{
    width:min(500px,calc(100vw - 32px));
    overflow:hidden;
    border:1px solid rgba(221,231,232,.96);
    border-radius:22px;
    background:#fff;
    box-shadow:0 30px 82px rgba(15,23,42,.22);
  }

  .ms-lifecycle-head{
    min-height:76px;
    padding:15px 16px;
    display:grid;
    grid-template-columns:38px minmax(0,1fr) 36px;
    align-items:center;
    gap:11px;
    border-bottom:1px solid #edf1f2;
  }

  .ms-lifecycle-icon{
    width:38px;
    height:38px;
    border-radius:13px;
    display:grid;
    place-items:center;
    background:#eaf8f5;
    color:#00776d;
    font-size:16px;
    font-weight:950;
  }

  .ms-lifecycle-icon.danger{
    background:#fff1ef;
    color:#b42318;
  }

  .ms-lifecycle-head h3{
    margin:0;
    color:#142739;
    font-size:16px;
    line-height:1.2;
  }

  .ms-lifecycle-head p{
    margin:3px 0 0;
    color:#6d7f90;
    font-size:10.5px;
    font-weight:700;
  }

  .ms-lifecycle-body{
    padding:16px;
    display:grid;
    gap:12px;
    color:#5e7083;
    font-size:11px;
    line-height:1.45;
  }

  .ms-lifecycle-loading{
    padding:18px;
    border:1px dashed #cfdfde;
    border-radius:14px;
    background:#f8fbfb;
    text-align:center;
    font-weight:750;
  }

  .ms-lifecycle-safe,
  .ms-lifecycle-warning{
    padding:12px;
    display:grid;
    gap:3px;
    border-radius:14px;
  }

  .ms-lifecycle-safe{
    border:1px solid rgba(0,125,114,.18);
    background:#f1faf8;
  }

  .ms-lifecycle-safe strong{
    color:#00776d;
  }

  .ms-lifecycle-warning{
    border:1px solid rgba(180,83,9,.18);
    background:#fff9f1;
  }

  .ms-lifecycle-warning strong{
    color:#9b580c;
  }

  .ms-lifecycle-safe span,
  .ms-lifecycle-warning span{
    color:#657789;
    font-size:10px;
    font-weight:650;
  }

  .ms-lifecycle-counts{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:6px;
  }

  .ms-lifecycle-counts > span{
    min-height:54px;
    padding:8px;
    border:1px solid #e3eaea;
    border-radius:11px;
    background:#fafcfc;
    display:grid;
    align-content:center;
    gap:2px;
    color:#728196;
    font-size:8.5px;
    font-weight:700;
  }

  .ms-lifecycle-counts strong{
    color:#1c3548;
    font-size:15px;
  }

  .ms-lifecycle-footer{
    padding:12px 16px 14px;
    display:flex;
    justify-content:flex-end;
    gap:8px;
    border-top:1px solid #edf1f2;
  }

  .ms-btn.danger-action{
    border-color:#b42318;
    background:#b42318;
    color:#fff;
  }

  .ms-btn.danger-action:hover{
    background:#931b14;
  }

  @media(max-width:780px){
    .ms-plan-actions{
      grid-template-columns:40px 1fr 1fr !important;
    }

    .ms-circle-action{
      display:grid !important;
      width:38px !important;
      height:38px !important;
    }

    .ms-plan-menu{
      left:0;
      right:auto;
      bottom:calc(100% + 7px);
    }
  }

  @media(max-width:520px){
    .ms-lifecycle-layer{
      align-items:end;
      padding:0;
    }

    .ms-lifecycle-modal{
      width:100%;
      border-radius:22px 22px 0 0;
    }

    .ms-lifecycle-counts{
      grid-template-columns:1fr 1fr;
    }

    .ms-lifecycle-footer{
      display:grid;
      grid-template-columns:1fr 1.2fr;
    }

    .ms-lifecycle-footer .ms-btn{
      width:100%;
    }
  }
`;
